/**
 * networkUtils.js — Shared utility functions for supply chain analytics views.
 */
import { fitShouldCost } from './shouldCost'

// Volume / price-break exponent. Fitted from data per process family (Phase 3).
// Fallback value 0.52 is the audit-measured within-part exponent (n=8,409).
// The EAU ratio is clamped to [1/VOLUME_RATIO_CAP, VOLUME_RATIO_CAP].
export const VOLUME_EXP = 0.52
export const VOLUME_RATIO_CAP = 100

/** Volume-adjust a reference price from refEau to targetEau (clamped).
 *  exp is passed as a positive magnitude; volAdjust returns >1 when ref>target. */
export function volAdjust(refEau, targetEau, exp = VOLUME_EXP) {
  if (!(refEau > 0) || !(targetEau > 0)) return 1
  const ratio = Math.min(VOLUME_RATIO_CAP, Math.max(1 / VOLUME_RATIO_CAP, refEau / targetEau))
  return Math.pow(ratio, Math.abs(exp))
}

/**
 * Fit the volume exponent per process family from WITHIN-PART price variation
 * (same part, different suppliers/sites — identification is clean because the
 * part is identical). Model: ln(uc) = a_part + b·ln(eau).
 * Returns { byMethod: {machining: -0.5, ...}, global: -0.52, nByMethod: {...}, fitted: true }
 */
export function fitVolumeExponents(parts) {
  // Collect demeaned (within-part) pairs of (ln_uc, ln_eau) per method
  const pairsByMethod = {}
  const allPairs = []

  parts.forEach(p => {
    const method = (p.method || '').toLowerCase().trim() || '__all'
    const lines = []

    // Gather lines from suppliers
    ;(p.suppliers || []).forEach(s => {
      const uc = Number(s.unit_cost) || 0
      const eau = parseFloat(s.eau) || 0
      if (uc > 0 && eau > 0) lines.push({ luc: Math.log(uc), leau: Math.log(eau) })
    })
    // Gather lines from sites
    ;(p.sites || []).forEach(s => {
      const uc = Number(s.unit_cost) || 0
      const eau = parseFloat(s.eau) || 0
      if (uc > 0 && eau > 0) lines.push({ luc: Math.log(uc), leau: Math.log(eau) })
    })

    if (lines.length < 2) return

    // Demean within part
    const meanLuc = lines.reduce((s, l) => s + l.luc, 0) / lines.length
    const meanLeau = lines.reduce((s, l) => s + l.leau, 0) / lines.length

    lines.forEach(l => {
      const pair = { dluc: l.luc - meanLuc, dleau: l.leau - meanLeau }
      if (!pairsByMethod[method]) pairsByMethod[method] = []
      pairsByMethod[method].push(pair)
      allPairs.push(pair)
    })
  })

  // Fit slope b = Σxy / Σxx per method bucket
  function fitSlope(pairs) {
    let sxy = 0, sxx = 0
    pairs.forEach(p => { sxy += p.dleau * p.dluc; sxx += p.dleau * p.dleau })
    if (sxx === 0) return null
    return sxy / sxx
  }

  // Global fit
  let globalExp = -0.52 // audit prior fallback
  if (allPairs.length >= 200) {
    const raw = fitSlope(allPairs)
    if (raw !== null) globalExp = Math.max(-0.9, Math.min(-0.05, raw))
  }

  // Per-method fits (need ≥50 pairs, else fall back to global)
  const byMethod = {}
  const nByMethod = {}
  Object.entries(pairsByMethod).forEach(([method, pairs]) => {
    nByMethod[method] = pairs.length
    if (pairs.length >= 50) {
      const raw = fitSlope(pairs)
      if (raw !== null) {
        byMethod[method] = Math.max(-0.9, Math.min(-0.05, raw))
      } else {
        byMethod[method] = globalExp
      }
    } else {
      byMethod[method] = globalExp
    }
  })

  return {
    byMethod,
    global: globalExp,
    nByMethod,
    nGlobal: allPairs.length,
    fitted: allPairs.length >= 200,
  }
}

export function formatEur(val) {
  if (val == null || val === 0) return '€0'
  if (val >= 1e6) return `€${(val / 1e6).toFixed(1)}M`
  if (val >= 1e3) return `€${(val / 1e3).toFixed(0)}K`
  return `€${val.toFixed(0)}`
}

export function formatEurFull(val) {
  if (val == null) return '€0'
  return `€${val.toLocaleString('de-DE', { maximumFractionDigits: 0 })}`
}

export function savingsRange(raw, multiplier) {
  if (multiplier != null) return { lo: raw * multiplier, hi: raw * multiplier }
  return { lo: raw * 0.5, hi: raw * 0.8 }
}

export function formatRange(raw, multiplier) {
  const r = savingsRange(raw, multiplier)
  if (multiplier != null) return formatEur(r.lo)
  return `${formatEur(r.lo)} – ${formatEur(r.hi)}`
}

/**
 * Compute price position for a supplier entry within a cohort.
 * Formula: benchmark = cohort_median_UC × volAdjust(cohort_median_EAU, part_EAU)
 * price_position = actual_unit_cost / benchmark
 */
// Cohort axes used for price position grouping (most to least specific)
// TODO(cohorts): consider cert_band/setup_band axes once cohort depth allows
const COHORT_AXES = ['method', 'material', 'size', 'tolerance_band', 'complexity_tier']

/** Check if a value represents an unknown/unspecified attribute */
function isUnknownValue(val) {
  if (!val) return true
  const lower = (val.toLowerCase ? val.toLowerCase() : String(val)).trim()
  if (!lower) return true
  if (lower === 'unknown' || lower === 'unassigned' || lower === 'n/a' || lower === 'none') return true
  if (lower.endsWith('_unspec') || lower.endsWith('unspec')) return true
  return false
}

/** Methods that produce cohorts too heterogeneous for price comparison */
const EXCLUDED_METHODS = new Set(['assembly'])

/** Translate raw cohort segment abbreviations into human-readable labels */
const COHORT_LABELS = {
  // Materials
  ti: 'Titanium', al: 'Aluminium', ss: 'Stainless Steel', cs: 'Carbon Steel',
  cu: 'Copper', ni: 'Nickel alloy', co: 'Cobalt alloy', w: 'Tungsten',
  br: 'Brass', bz: 'Bronze', mg: 'Magnesium', zn: 'Zinc',
  inconel: 'Inconel (Ni superalloy)', hastelloy: 'Hastelloy (Ni alloy)',
  peek: 'PEEK (polymer)', ptfe: 'PTFE', pom: 'POM (Delrin)',
  // Size envelope
  xs: 'Extra-small envelope', s: 'Small envelope', m: 'Medium envelope',
  l: 'Large envelope', xl: 'Extra-large envelope', xxl: 'Very large envelope',
  // Tolerance band
  ultra: 'Ultra-tight tolerance', precision: 'Precision tolerance',
  commercial: 'Commercial tolerance', tight: 'Tight tolerance',
  standard: 'Standard tolerance', loose: 'Loose tolerance',
  // Complexity (tiers + legacy low/med/high)
  simple: 'Low complexity', moderate: 'Moderate complexity', complex: 'High complexity',
  very_complex: 'Very high complexity',
  low: 'Low complexity', medium: 'Medium complexity',
  high: 'High complexity', very_high: 'Very high complexity',
  // Process / method
  machining: 'Machining', milling: 'Milling', turning: 'Turning', mill_turn: 'Mill-Turn',
  sheet_metal: 'Sheet metal', casting: 'Casting', forging: 'Forging', welding: 'Welding',
  grinding: 'Grinding', other: 'Other',
}

function humanizeCohortSegment(segment) {
  const trimmed = (segment || '').trim()
  const key = trimmed.toLowerCase().replace(/[\s-]+/g, '_')
  return COHORT_LABELS[key] || trimmed
}

/** Convert a raw cohort name (pipe-delimited or __-delimited) to readable form */
export function humanizeCohortName(raw) {
  if (!raw) return '\u2014'
  return raw.split(/\s*\|\s*|__/).map(humanizeCohortSegment).join(', ')
}

// \u2500\u2500\u2500 Human-legible part-family labels \u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500
// The family grouping key joins raw attribute tokens (method|material|size|
// tolerance_band|complexity_tier), e.g. "machining|Al|S|precision|moderate".
// New users can't read "Al + S + precision + moderate", so we expand each token
// position-by-position and add the category it describes (tolerance, complexity)
// where the bare adjective is ambiguous.
const FAMILY_METHOD_LABELS = {
  machining: 'Machining', milling: 'Milling', turning: 'Turning',
  mill_turn: 'Mill-Turn', sheet_metal: 'Sheet metal', casting: 'Casting',
  forging: 'Forging', welding: 'Welding', grinding: 'Grinding', other: 'Other',
}
const FAMILY_MATERIAL_LABELS = {
  al: 'Aluminum', aluminum: 'Aluminum', aluminium: 'Aluminum',
  ti: 'Titanium', titanium: 'Titanium',
  ss: 'Stainless steel', stainless: 'Stainless steel', stainless_steel: 'Stainless steel',
  cs: 'Steel', steel: 'Steel', carbon_steel: 'Steel',
  cu: 'Copper', copper: 'Copper', ni: 'Nickel alloy', co: 'Cobalt alloy',
  br: 'Brass', brass: 'Brass', bz: 'Bronze', bronze: 'Bronze',
  mg: 'Magnesium', zn: 'Zinc', w: 'Tungsten',
  polymer: 'Polymer', plastic: 'Polymer', peek: 'PEEK', ptfe: 'PTFE', pom: 'POM (Delrin)',
  inconel: 'Inconel', hastelloy: 'Hastelloy',
}
const FAMILY_SIZE_LABELS = {
  xs: 'Miniature', micro: 'Miniature', miniature: 'Miniature',
  s: 'Small', small: 'Small', m: 'Medium', medium: 'Medium',
  l: 'Large', large: 'Large', xl: 'Oversize', xxl: 'Oversize', oversize: 'Oversize',
}
const FAMILY_TOLERANCE_LABELS = {
  ultra: 'Ultra-tight tolerance', precision: 'Precision tolerance',
  commercial: 'Commercial tolerance', tight: 'Tight tolerance',
  standard: 'Standard tolerance', loose: 'Loose tolerance',
}
const FAMILY_COMPLEXITY_LABELS = {
  simple: 'Low complexity', moderate: 'Moderate complexity',
  complex: 'High complexity', very_complex: 'Very high complexity',
  low: 'Low complexity', medium: 'Moderate complexity',
  high: 'High complexity', very_high: 'Very high complexity',
}
const FAMILY_AXIS_LABELS = {
  '2axis': '3-axis or less', '3axis': '3-axis', '4axis': '4-axis',
  '5axis': '5-axis', '5axis_plus': '5-axis+', lathe: 'Lathe', swiss: 'Swiss-type',
}
// field name \u2192 value-label map. Used to humanize any single part attribute.
const ATTR_LABEL_MAPS = {
  method: FAMILY_METHOD_LABELS, process_family: FAMILY_METHOD_LABELS,
  material: FAMILY_MATERIAL_LABELS,
  size: FAMILY_SIZE_LABELS, size_band: FAMILY_SIZE_LABELS,
  tolerance_band: FAMILY_TOLERANCE_LABELS,
  complexity_tier: FAMILY_COMPLEXITY_LABELS,
  axis_tier: FAMILY_AXIS_LABELS,
}
// Order + fields that make up a part-family label.
const FAMILY_FIELDS = ['method', 'material', 'size', 'tolerance_band', 'complexity_tier']

function titleCaseToken(v) {
  return String(v || '').replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())
}

/** Human-readable label for a single part attribute, e.g. ('tolerance_band',
 * 'precision') \u2192 'Precision tolerance'. Falls back to title-case for unmapped
 * values; returns '' for unknown/unspecified values. */
export function humanizeAttr(field, value) {
  if (isUnknownValue(value)) return ''
  const map = ATTR_LABEL_MAPS[field]
  const key = String(value).toLowerCase().trim().replace(/[\s-]+/g, '_')
  return (map && map[key]) || titleCaseToken(value)
}

/**
 * Human-readable label for a part family, derived from a representative part's
 * known attributes. Skips unknown attributes (same filter as the grouping key)
 * so the label always matches the family. e.g. a part with
 * {method:'machining', material:'Al', size:'S', tolerance_band:'precision',
 *  complexity_tier:'moderate'} \u2192 "Machining \u00b7 Aluminum \u00b7 Small \u00b7 Precision
 * tolerance \u00b7 Moderate complexity".
 */
export function humanizeFamilyLabel(part) {
  return FAMILY_FIELDS
    .filter(field => !isUnknownValue(part[field]))
    .map(field => humanizeAttr(field, part[field]))
    .join(' \u00b7 ')
}

/**
 * Compute similarity score between two parts using the same logic as the backend.
 * Scoring: size_band/size (+4), material (+3), process/method (+3),
 *          tolerance_band (+2), complexity_tier (+2), envelope/shape (+2),
 *          machining_route (+1), dimensional proximity (+3) = max 20
 * Returns { score, pct, reasons: [{field, value, weight}], diffs: [{field, a, b}] }
 */
export function computeSimilarity(refPart, candidate) {
  const MAX_SCORE = 20
  let score = 0
  const reasons = []
  const diffs = []

  // Size / size_band (+4)
  const refSize = refPart.size || refPart.size_band || ''
  const candSize = candidate.size || candidate.size_band || ''
  if (refSize && candSize && refSize.toLowerCase() === candSize.toLowerCase()) {
    score += 4; reasons.push({ field: 'size', value: refSize, weight: 4 })
  } else if (refSize || candSize) {
    diffs.push({ field: 'size', a: refSize || '\u2014', b: candSize || '\u2014' })
  }

  // Material (+3)
  const refMat = refPart.material || refPart.material_family || ''
  const candMat = candidate.material || candidate.material_family || ''
  if (refMat && candMat && refMat.toLowerCase() === candMat.toLowerCase()) {
    score += 3; reasons.push({ field: 'material', value: refMat, weight: 3 })
  } else if (refMat || candMat) {
    diffs.push({ field: 'material', a: refMat || '\u2014', b: candMat || '\u2014' })
  }

  // Process / method (+3)
  const refProc = refPart.method || refPart.process_family || refPart.primary_process || ''
  const candProc = candidate.method || candidate.process_family || candidate.primary_process || ''
  if (refProc && candProc && refProc.toLowerCase() === candProc.toLowerCase()) {
    score += 3; reasons.push({ field: 'process', value: refProc, weight: 3 })
  } else if (refProc || candProc) {
    diffs.push({ field: 'process', a: refProc || '\u2014', b: candProc || '\u2014' })
  }

  // Tolerance band (+2)
  const refTol = refPart.tolerance_band || ''
  const candTol = candidate.tolerance_band || ''
  if (refTol && candTol && !isUnknownValue(refTol) && refTol.toLowerCase() === candTol.toLowerCase()) {
    score += 2; reasons.push({ field: 'tolerance', value: refTol, weight: 2 })
  } else if (refTol && candTol && !isUnknownValue(refTol) && !isUnknownValue(candTol) && refTol !== candTol) {
    diffs.push({ field: 'tolerance', a: refTol, b: candTol })
  }

  // Complexity tier (+2)
  const refComp = refPart.complexity_tier || ''
  const candComp = candidate.complexity_tier || ''
  if (refComp && candComp && !isUnknownValue(refComp) && refComp.toLowerCase() === candComp.toLowerCase()) {
    score += 2; reasons.push({ field: 'complexity', value: refComp, weight: 2 })
  } else if (refComp && candComp && !isUnknownValue(refComp) && !isUnknownValue(candComp) && refComp !== candComp) {
    diffs.push({ field: 'complexity', a: refComp, b: candComp })
  }

  // Envelope / shape (+2)
  const refEnv = refPart.envelope_class || refPart.shape_class || ''
  const candEnv = candidate.envelope_class || candidate.shape_class || ''
  if (refEnv && candEnv && !isUnknownValue(refEnv) && refEnv.toLowerCase() === candEnv.toLowerCase()) {
    score += 2; reasons.push({ field: 'envelope', value: refEnv, weight: 2 })
  } else if (refEnv && candEnv && !isUnknownValue(refEnv) && !isUnknownValue(candEnv) && refEnv !== candEnv) {
    diffs.push({ field: 'envelope', a: refEnv, b: candEnv })
  }

  // Machining route (+1)
  const refRoute = refPart.machining_route || ''
  const candRoute = candidate.machining_route || ''
  if (refRoute && candRoute && refRoute.toLowerCase() === candRoute.toLowerCase()) {
    score += 1; reasons.push({ field: 'route', value: refRoute, weight: 1 })
  }

  // Dimensional proximity (+3) — based on tightest_mm as a proxy
  if (refPart.tightest_mm != null && candidate.tightest_mm != null) {
    const ref = Number(refPart.tightest_mm)
    const cand = Number(candidate.tightest_mm)
    if (ref > 0) {
      const ratio = Math.abs(ref - cand) / ref
      if (ratio <= 0.10) { score += 3; reasons.push({ field: 'tolerance_value', value: `within ${Math.round(ratio * 100)}%`, weight: 3 }) }
      else if (ratio <= 0.25) { score += 2; reasons.push({ field: 'tolerance_value', value: `within ${Math.round(ratio * 100)}%`, weight: 2 }) }
      else if (ratio <= 0.50) { score += 1; reasons.push({ field: 'tolerance_value', value: `within ${Math.round(ratio * 100)}%`, weight: 1 }) }
      else { diffs.push({ field: 'tolerance_value', a: ref.toFixed(3) + 'mm', b: cand.toFixed(3) + 'mm' }) }
    }
  }

  return { score, pct: Math.round(score / MAX_SCORE * 100), reasons, diffs }
}

/**
 * Build a cohort key from a part's known attributes only.
 * Skips any axis where the value is unknown/unspecified.
 * Returns null if fewer than MIN_COHORT_ATTRS known attributes exist.
 */
const MIN_COHORT_ATTRS = 2

function buildCohortKey(p) {
  // Exclude parts whose method produces heterogeneous cohorts
  const method = p.method
  if (method && EXCLUDED_METHODS.has(method.toLowerCase().trim())) return null
  const knownParts = []
  for (const ax of COHORT_AXES) {
    const val = p[ax]
    if (!isUnknownValue(val)) {
      knownParts.push(val)
    }
  }
  return knownParts.length >= MIN_COHORT_ATTRS ? knownParts.join(' | ') : null
}

/** Count number of known cohort attributes on a part */
export function cohortAttrCount(p) {
  let count = 0
  for (const ax of COHORT_AXES) {
    if (!isUnknownValue(p[ax])) count++
  }
  return count
}

export function computePricePositions(parts) {
  // Fit volume exponents from the data (Phase 3)
  const volStats = fitVolumeExponents(parts)

  // Group by cohort built from known attributes
  const cohorts = {}
  parts.forEach(p => {
    const coh = buildCohortKey(p)
    if (!coh) return // no known attributes — cannot benchmark
    if (!cohorts[coh]) cohorts[coh] = []
    p.suppliers.forEach(s => {
      if (s.unit_cost > 0) {
        cohorts[coh].push({
          unit_cost: s.unit_cost,
          eau: parseFloat(s.eau) || 0,
          supplier: s.name,
          pn: p.pn || p.filename || '',
          description: p.description || '',
          file_id: p.file_id || null,
          material: p.material || '',
          method: p.method || '',
          setup_count: p.setup_count || null,
          dim_count: p.dim_count || null,
        })
      }
    })
  })

  // Compute cohort medians
  const cohortStats = {}
  Object.entries(cohorts).forEach(([coh, entries]) => {
    if (entries.length < 3) return
    const costs = entries.map(e => e.unit_cost).sort((a, b) => a - b)
    const eaus = entries.filter(e => e.eau > 0).map(e => e.eau).sort((a, b) => a - b)
    const p25 = costs[Math.floor(costs.length * 0.25)]
    const p75 = costs[Math.floor(costs.length * 0.75)]
    cohortStats[coh] = {
      median_uc: costs[Math.floor(costs.length / 2)],
      median_eau: eaus.length > 0 ? eaus[Math.floor(eaus.length / 2)] : 0,
      p25_uc: p25,
      p75_uc: p75,
      dispersion: p25 > 0 ? p75 / p25 : Infinity,
      entries,
    }
  })

  // Fit regression should-cost model (Phase 4)
  const scModel = fitShouldCost(parts, buildCohortKey)

  // Assign price positions
  const enriched = parts.map(p => {
    const coh = buildCohortKey(p)
    if (!coh) return p // no cohort — cannot benchmark
    const stats = cohortStats[coh]
    if (!stats) return p

    const attrCount = cohortAttrCount(p)
    const method = (p.method || '').toLowerCase().trim()
    const volExp = Math.abs(volStats.byMethod[method] || volStats.global)

    const enrichedSuppliers = p.suppliers.map(s => {
      if (!s.unit_cost || s.unit_cost <= 0) return s
      const eau = parseFloat(s.eau) || 0
      if (eau <= 0 || stats.median_eau <= 0) return s

      const benchmark = stats.median_uc * volAdjust(stats.median_eau, eau, volExp)
      const pp = s.unit_cost / benchmark

      // Should-cost prediction (Phase 4)
      let should_cost = null, sc_residual = null, price_position_v2 = null
      if (scModel) {
        const sc = scModel.predict({ ...p, eau: s.eau }, coh)
        if (sc && sc > 0) {
          should_cost = Math.round(sc * 100) / 100
          sc_residual = Math.log(s.unit_cost / sc)
          price_position_v2 = Math.round((s.unit_cost / sc) * 100) / 100
        }
      }

      return {
        ...s,
        price_position: Math.round(pp * 100) / 100,
        benchmark: Math.round(benchmark * 100) / 100,
        vol_exp: volExp,
        should_cost,
        sc_residual,
        sc_sigma: scModel ? scModel.sigma : null,
        price_position_v2,
        cohort_median_uc: Math.round(stats.median_uc * 100) / 100,
        cohort_median_eau: Math.round(stats.median_eau),
        cohort_p25_uc: Math.round(stats.p25_uc * 100) / 100,
        cohort_p75_uc: Math.round(stats.p75_uc * 100) / 100,
        cohort_dispersion: Math.round(stats.dispersion * 100) / 100,
        cohort_size: stats.entries.length,
        cohort_entries: stats.entries,
        cohort_name: coh,
        cohort_attr_count: attrCount,
      }
    })

    return { ...p, suppliers: enrichedSuppliers, cohort_attr_count: attrCount, _volExp: volExp }
  })

  // Attach volume stats and should-cost model to the result for downstream consumers
  enriched._volStats = volStats
  enriched._scModel = scModel
  return enriched
}

// ─── Negotiation Strategy Scoring ─────────────────────────────────────────────
// Scores 6 classical procurement strategies for a given negotiation opportunity.
// Each strategy scores 0-100. Returns sorted array, highest first.

const NEGO_STRATEGIES = [
  { id: 'benchmarking', name: 'Benchmarking / Should-Cost', icon: '\u{1F4CA}',
    desc: 'Present market data showing the supplier is above benchmark. Data-driven, low conflict.' },
  { id: 'volume_bundling', name: 'Volume Bundling', icon: '\u{1F4E6}',
    desc: 'Consolidate all affected parts into a single negotiation package. Reward alignment with commitment.' },
  { id: 'dual_sourcing', name: 'Dual-Sourcing', icon: '\u2696\uFE0F',
    desc: 'Qualify a second source for high-spend items. Structural leverage for future negotiations.' },
  { id: 'partnership_lta', name: 'Partnership / LTA', icon: '\u{1F91D}',
    desc: 'Offer long-term volume commitment in exchange for pricing concession. Best when relationship is valuable.' },
  { id: 'spec_optimization', name: 'Specification Optimization', icon: '\u2699\uFE0F',
    desc: 'Review tolerances, materials, and complexity. Reduce cost drivers rather than squeezing margin.' },
]

export function scoreNegotiationStrategies(g, goodItems, parts) {
  const items = g.items // array of { part, supplier, overspend }
  const itemCount = items.length
  const avgPP = items.reduce((s, i) => s + i.supplier.price_position, 0) / itemCount
  const totalSpend = g.totalSpend

  // Compute signals from the data
  // 1. Cohort richness: avg cohort size across items (more entries = more market data)
  const cohortSizes = items.map(i => i.supplier.cohort_size || 0).filter(c => c > 0)
  const avgCohortSize = cohortSizes.length > 0 ? cohortSizes.reduce((s, c) => s + c, 0) / cohortSizes.length : 0

  // 2. Alternative suppliers: count distinct other suppliers seen in cohort entries
  const altSuppliers = new Set()
  items.forEach(i => {
    (i.supplier.cohort_entries || []).forEach(e => {
      if (e.supplier && e.supplier !== g.supplier) altSuppliers.add(e.supplier)
    })
  })
  const altCount = altSuppliers.size

  // 3. Sole-source ratio: % of parts where this is the only priced supplier
  const soleSourced = items.filter(i => {
    const pricedSuppliers = i.part.suppliers.filter(s => s.unit_cost > 0)
    return pricedSuppliers.length === 1
  }).length
  const soleRatio = soleSourced / itemCount

  // 4. Part complexity: ratio of parts with tight tolerances or high complexity
  const complexParts = items.filter(i => {
    const tol = (i.part.tolerance_band || '').toLowerCase()
    const comp = (i.part.complexity_tier || '').toLowerCase()
    return tol.includes('tight') || comp.includes('high') || comp.includes('very')
  }).length
  const complexRatio = complexParts / itemCount

  // 5. Competitive items from same supplier
  const goodCount = goodItems.length
  const goodSpend = goodItems.reduce((s, gi) => s + gi.spend, 0)

  // 6. Spend concentration
  const spendHigh = totalSpend > 100000
  const spendVeryHigh = totalSpend > 500000

  // ─── Score each strategy ───
  const scores = []

  // BENCHMARKING: strong when good data exists + supplier has competitive items
  let benchScore = 30 // baseline: we always have benchmarks
  const benchFactors = [{ signal: 'Baseline (benchmarks exist)', pts: 30 }]
  if (avgCohortSize >= 5) { benchScore += 20; benchFactors.push({ signal: `Large cohorts (avg ${avgCohortSize.toFixed(1)} members)`, pts: 20 }) }
  else if (avgCohortSize >= 3) { benchScore += 10; benchFactors.push({ signal: `Moderate cohorts (avg ${avgCohortSize.toFixed(1)} members)`, pts: 10 }) }
  if (goodCount > 0) { benchScore += 20; benchFactors.push({ signal: `${goodCount} competitive items prove supplier CAN price fairly`, pts: 20 }) }
  if (avgPP >= 1.3 && avgPP <= 2.0) { benchScore += 15; benchFactors.push({ signal: `Credible range (${avgPP.toFixed(2)}x avg) — not absurd`, pts: 15 }) }
  if (avgPP > 2.0) { benchScore += 5; benchFactors.push({ signal: `Extreme gap (${avgPP.toFixed(2)}x) — data useful but may be dismissed`, pts: 5 }) }
  if (altCount >= 3) { benchScore += 10; benchFactors.push({ signal: `${altCount} alt suppliers in market validate benchmarks`, pts: 10 }) }
  benchScore = Math.min(100, benchScore)

  // VOLUME BUNDLING: strong when many parts, high total spend
  let bundleScore = 10
  const bundleFactors = [{ signal: 'Baseline', pts: 10 }]
  if (itemCount >= 8) { bundleScore += 25; bundleFactors.push({ signal: `Large bundle (${itemCount} parts)`, pts: 25 }) }
  else if (itemCount >= 5) { bundleScore += 18; bundleFactors.push({ signal: `Moderate bundle (${itemCount} parts)`, pts: 18 }) }
  else if (itemCount >= 3) { bundleScore += 10; bundleFactors.push({ signal: `Small bundle (${itemCount} parts)`, pts: 10 }) }
  if (spendVeryHigh) { bundleScore += 25; bundleFactors.push({ signal: `Very high spend (${formatEur(totalSpend)})`, pts: 25 }) }
  else if (spendHigh) { bundleScore += 18; bundleFactors.push({ signal: `High spend (${formatEur(totalSpend)})`, pts: 18 }) }
  else if (totalSpend > 50000) { bundleScore += 10; bundleFactors.push({ signal: `Moderate spend (${formatEur(totalSpend)})`, pts: 10 }) }
  if (goodCount > 0) { bundleScore += 10; bundleFactors.push({ signal: `${goodCount} good items = "bringing ALL the business"`, pts: 10 }) }
  if (avgPP < 1.8) { bundleScore += 10; bundleFactors.push({ signal: `Gap not extreme (${avgPP.toFixed(2)}x) — supplier likely to engage`, pts: 10 }) }
  bundleScore = Math.min(100, bundleScore)

  // DUAL-SOURCING: strong when sole-sourced + some alternates exist
  let dualScore = 5
  const dualFactors = [{ signal: 'Baseline', pts: 5 }]
  if (soleRatio > 0.7) { dualScore += 25; dualFactors.push({ signal: `High sole-source (${Math.round(soleRatio * 100)}% single-sourced)`, pts: 25 }) }
  else if (soleRatio > 0.4) { dualScore += 15; dualFactors.push({ signal: `Moderate sole-source (${Math.round(soleRatio * 100)}%)`, pts: 15 }) }
  if (altCount >= 2) { dualScore += 20; dualFactors.push({ signal: `${altCount} alternate suppliers exist to qualify`, pts: 20 }) }
  else if (altCount >= 1) { dualScore += 10; dualFactors.push({ signal: `${altCount} alternate supplier identified`, pts: 10 }) }
  if (spendHigh) { dualScore += 15; dualFactors.push({ signal: `High spend justifies qualification cost`, pts: 15 }) }
  if (avgPP > 1.5) { dualScore += 15; dualFactors.push({ signal: `Severe gap (${avgPP.toFixed(2)}x) warrants structural change`, pts: 15 }) }
  if (complexRatio > 0.5) { dualScore -= 10; dualFactors.push({ signal: `High complexity (${Math.round(complexRatio * 100)}%) — hard to qualify alternates`, pts: -10 }) }
  dualScore = Math.max(0, Math.min(100, dualScore))

  // PARTNERSHIP / LTA: strong when large spend, moderate overpricing, valuable relationship
  let ltaScore = 10
  const ltaFactors = [{ signal: 'Baseline', pts: 10 }]
  if (spendVeryHigh) { ltaScore += 25; ltaFactors.push({ signal: `Very high spend (${formatEur(totalSpend)}) — worth a deal`, pts: 25 }) }
  else if (spendHigh) { ltaScore += 18; ltaFactors.push({ signal: `High spend (${formatEur(totalSpend)})`, pts: 18 }) }
  else if (totalSpend > 50000) { ltaScore += 10; ltaFactors.push({ signal: `Moderate spend (${formatEur(totalSpend)})`, pts: 10 }) }
  if (goodCount >= 3) { ltaScore += 20; ltaFactors.push({ signal: `${goodCount} competitive items — relationship worth preserving`, pts: 20 }) }
  else if (goodCount > 0) { ltaScore += 10; ltaFactors.push({ signal: `${goodCount} competitive item(s) — some relationship value`, pts: 10 }) }
  if (avgPP < 1.5) { ltaScore += 15; ltaFactors.push({ signal: `Moderate gap (${avgPP.toFixed(2)}x) — partnership can fix`, pts: 15 }) }
  else if (avgPP < 1.8) { ltaScore += 5; ltaFactors.push({ signal: `Reasonable gap (${avgPP.toFixed(2)}x)`, pts: 5 }) }
  if (itemCount >= 5) { ltaScore += 10; ltaFactors.push({ signal: `Deep relationship (${itemCount} parts)`, pts: 10 }) }
  if (soleRatio > 0.5) { ltaScore += 10; ltaFactors.push({ signal: `High dependency (${Math.round(soleRatio * 100)}% sole-sourced) — better to partner`, pts: 10 }) }
  ltaScore = Math.min(100, ltaScore)

  // SPEC OPTIMIZATION: strong when parts are complex/tight tolerance
  let specScore = 5
  const specFactors = [{ signal: 'Baseline', pts: 5 }]
  if (complexRatio > 0.6) { specScore += 30; specFactors.push({ signal: `High complexity ratio (${Math.round(complexRatio * 100)}% complex parts)`, pts: 30 }) }
  else if (complexRatio > 0.3) { specScore += 15; specFactors.push({ signal: `Moderate complexity (${Math.round(complexRatio * 100)}% complex)`, pts: 15 }) }
  // Check for expensive materials
  const expensiveMats = items.filter(i => {
    const mat = (i.part.material || '').toLowerCase()
    return mat.includes('titanium') || mat.includes('inconel') || mat.includes('hastelloy') || mat.includes('nickel') || mat.includes('cobalt') || mat.includes('tungsten')
  }).length
  if (expensiveMats > 0) { specScore += 20; specFactors.push({ signal: `${expensiveMats} parts use exotic materials (Ti, Inconel, etc.)`, pts: 20 }) }
  if (avgPP > 2.0) { specScore += 15; specFactors.push({ signal: `Extreme gap (${avgPP.toFixed(2)}x) may be spec-driven`, pts: 15 }) }
  if (avgPP > 1.5 && complexRatio > 0.3) { specScore += 10; specFactors.push({ signal: `Overpricing + complexity suggests spec review opportunity`, pts: 10 }) }
  // Low confidence if we don't have good attribute data
  const avgAttrCount = items.reduce((s, i) => s + (cohortAttrCount(i.part) || 0), 0) / itemCount
  if (avgAttrCount < 3) { specScore -= 10; specFactors.push({ signal: `Low attribute data (avg ${avgAttrCount.toFixed(1)} attrs) — can't assess specs`, pts: -10 }) }
  specScore = Math.max(0, Math.min(100, specScore))

  // Assemble and sort
  const strategyScores = [
    { ...NEGO_STRATEGIES[0], score: benchScore, factors: benchFactors },
    { ...NEGO_STRATEGIES[1], score: bundleScore, factors: bundleFactors },
    { ...NEGO_STRATEGIES[2], score: dualScore, factors: dualFactors },
    { ...NEGO_STRATEGIES[3], score: ltaScore, factors: ltaFactors },
    { ...NEGO_STRATEGIES[4], score: specScore, factors: specFactors },
  ].sort((a, b) => b.score - a.score)

  return strategyScores
}

/**
 * Detect savings opportunities from parts data.
 * @param {Array} parts - enriched parts with price positions
 * @param {Object} params - tunable parameters
 * @param {number} params.priceThreshold - PP cutoff for negotiation (default 1.3)
 * @param {number} params.minSize - minimum savings in EUR to surface (default 500)
 * @param {number} params.outlierThreshold - ratio above which to flag as data error (default 3)
 * @param {number} params.similarMinAttrs - min shared attributes for similar-part gap (default 4)
 */

// ─── Division Preference Utilities ────────────────────────────────────────────

const DIVISION_BOOST = 0.5 // max 50% ranking boost for 100% affinity
const PREFERRED_THRESHOLD_FACTOR = 0.85 // 15% lower PP threshold for preferred division

/**
 * Compute division affinity for an opportunity: what fraction of its spend
 * belongs to the preferred division's sites.
 */
export function computeDivisionAffinity(opp, preferredDivision, siteToDivision) {
  if (!siteToDivision) return { affinity: 0, breakdown: {}, totalSpend: 0 }

  let totalSpend = 0
  const divSpend = {}
  ;(opp.parts || []).forEach(p => {
    ;(p.sites || []).forEach(s => {
      const div = siteToDivision[s.site]
      if (!div) return
      divSpend[div] = (divSpend[div] || 0) + (s.spend || 0)
      totalSpend += (s.spend || 0)
    })
    // Fallback: if part has no sites array, check suppliers' sites
    if (!(p.sites || []).length && (p.suppliers || []).length) {
      p.suppliers.forEach(s => {
        ;(s.sites || []).forEach(site => {
          const div = siteToDivision[site]
          if (!div) return
          divSpend[div] = (divSpend[div] || 0) + ((s.spend || 0) / (s.sites || [site]).length)
          totalSpend += ((s.spend || 0) / (s.sites || [site]).length)
        })
      })
    }
  })

  const affinity = (preferredDivision && totalSpend > 0) ? (divSpend[preferredDivision] || 0) / totalSpend : 0
  return { affinity, breakdown: divSpend, totalSpend }
}

/**
 * Compute per-division savings split for an opportunity.
 */
export function computeDivisionSavings(opp, siteToDivision) {
  if (!siteToDivision) return {}
  const { breakdown, totalSpend } = computeDivisionAffinity(opp, null, siteToDivision)
  if (totalSpend <= 0) return {}
  const result = {}
  Object.entries(breakdown).forEach(([div, spend]) => {
    result[div] = opp.estSavings * (spend / totalSpend)
  })
  return result
}

/**
 * Determine effective price threshold for a part based on division preference.
 * Preferred-division parts get a lower threshold (surfaces more opportunities).
 */
function effectivePriceThreshold(part, baseThreshold, preferredDivision, siteToDivision) {
  if (!preferredDivision || !siteToDivision) return baseThreshold
  const partSites = (part.sites || []).map(s => s.site)
  const inPreferred = partSites.some(s => siteToDivision[s] === preferredDivision)
  return inPreferred ? baseThreshold * PREFERRED_THRESHOLD_FACTOR : baseThreshold
}

function effectiveMinSize(part, baseMin, preferredDivision, siteToDivision) {
  if (!preferredDivision || !siteToDivision) return baseMin
  const partSites = (part.sites || []).map(s => s.site)
  const inPreferred = partSites.some(s => siteToDivision[s] === preferredDivision)
  return inPreferred ? baseMin * 0.6 : baseMin // $300 instead of $500
}

// ─── Main Opportunity Computation ─────────────────────────────────────────────

export function computeOpportunities(parts, params = {}) {
  const {
    priceThreshold = 1.3,
    negotiationTarget = 1.1,
    minSize = 500,
    outlierThreshold = 3,
    similarMinAttrs = 4,
    dispersionMax = 2.5, // max cohort P75/P25 price ratio for negotiation-grade benchmarks
    minCohortSize = 5, // min priced entries in cohort for negotiation-grade benchmarks
    validateRatio = 4.0, // price ratios >= this are quarantined as probable UoM/data errors
    familySavingsCap = 0.25, // max savings as fraction of family spend (Phase 2c)
    sigmaThreshold = 1.0, // residuals above this many σ trigger negotiation (Phase 4)
    divisionPreference = null,
    siteToDivision = null,
  } = params

  const opps = []

  // 1. Negotiation targets: identify overpriced supplier lines.
  // Phase 4: when should-cost model is available, use sc_residual > sigmaThreshold·σ
  // as the primary gate. Falls back to PP > priceThreshold when model is null.
  // Gate by cohort match quality: only benchmark against cohorts where >= similarMinAttrs
  // of the 5 attributes matched.
  const negoTargets = []
  const hasScModel = parts._scModel != null
  parts.forEach(p => {
    const effThreshold = effectivePriceThreshold(p, priceThreshold, divisionPreference, siteToDivision)
    const effMinSize = effectiveMinSize(p, minSize, divisionPreference, siteToDivision)
    p.suppliers.forEach(s => {
      if (!s.spend || s.spend <= effMinSize) return
      if ((s.cohort_attr_count || 0) < similarMinAttrs) return
      if ((s.cohort_size || 0) < minCohortSize) return
      if (isUnknownValue(p.tolerance_band) || isUnknownValue(p.complexity_tier)) return

      const uc = Number(s.unit_cost) || 0
      if (uc <= 0) return
      const eau = parseFloat(s.eau) || 0
      const volExp = s.vol_exp || p._volExp || VOLUME_EXP

      // Determine if this line is overpriced
      let isOverpriced = false
      if (hasScModel && s.sc_residual != null && s.sc_sigma != null && s.sc_sigma > 0) {
        // v2: residual-based gate
        isOverpriced = s.sc_residual > sigmaThreshold * s.sc_sigma
      } else if (s.price_position && s.price_position > effThreshold
          && (s.cohort_dispersion || Infinity) <= dispersionMax) {
        // v1 fallback: PP-based gate
        isOverpriced = true
      }
      if (!isOverpriced) return

      // Target = max(should_cost * nt, volume-adjusted cheapest REAL peer)
      const scTarget = (s.should_cost && s.should_cost > 0)
        ? s.should_cost * negotiationTarget : null
      const rawTarget = (s.benchmark || 0) * negotiationTarget
      const realPeers = (s.cohort_entries || []).filter(e => e.unit_cost > 0)
      const cheapest = realPeers.length
        ? realPeers.reduce((a, b) => (a.unit_cost < b.unit_cost ? a : b))
        : null
      const peerFloor = cheapest ? cheapest.unit_cost * volAdjust(cheapest.eau || 0, eau, volExp) : (scTarget || rawTarget)
      const target = Math.max(scTarget || rawTarget, peerFloor)
      if (uc <= target) return // floored target removes the opportunity — skip
      const overspend = s.spend * (1 - target / uc)
      negoTargets.push({ part: p, supplier: s, overspend, target })
    })
  })

  // Quarantine negotiation items with extreme price ratios (Phase 1a)
  negoTargets.forEach(t => {
    const uc = Number(t.supplier.unit_cost) || 0
    const pp = t.supplier.price_position || 0
    const realPeers = (t.supplier.cohort_entries || []).filter(e => e.unit_cost > 0)
    const cheapestPeer = realPeers.length
      ? realPeers.reduce((a, b) => (a.unit_cost < b.unit_cost ? a : b))
      : null
    const peerRatio = cheapestPeer && cheapestPeer.unit_cost > 0 ? uc / cheapestPeer.unit_cost : 0
    if (pp >= validateRatio || peerRatio >= validateRatio * 2) {
      t.quarantined = true
      t.quarantineReason = 'Price ratio ' + (pp >= validateRatio ? pp.toFixed(1) : peerRatio.toFixed(1)) + '× — probable unit-of-measure / pack-size / revision mismatch. Verify with buyer before counting.'
    }
  })

  // Group by supplier
  const negoBySupplier = {}
  negoTargets.forEach(t => {
    const key = t.supplier.name
    if (!negoBySupplier[key]) negoBySupplier[key] = { supplier: key, items: [], validateItems: [], totalOverspend: 0, totalSpend: 0 }
    if (t.quarantined) {
      negoBySupplier[key].validateItems.push(t)
    } else {
      negoBySupplier[key].items.push(t)
      negoBySupplier[key].totalOverspend += t.overspend
    }
    negoBySupplier[key].totalSpend += t.supplier.spend
  })

  Object.values(negoBySupplier).sort((a, b) => b.totalOverspend - a.totalOverspend).forEach(g => {
    if (g.totalOverspend < minSize && g.validateItems.length === 0) return
    if (g.items.length === 0 && g.validateItems.length === 0) return
    const allQuarantined = g.items.length === 0
    if (!allQuarantined && g.totalOverspend < minSize) return
    const uniqueParts = [...new Map(g.items.map(i => [i.part.pn, i.part])).values()]
    const avgPP = g.items.length > 0 ? g.items.reduce((s, i) => s + i.supplier.price_position, 0) / g.items.length : 0

    // Gather competitively-priced items from same supplier (PP ≤ threshold, valid PP)
    const overPNs = new Set(g.items.map(i => i.part.pn))
    const goodItems = []
    parts.forEach(p => {
      if (overPNs.has(p.pn)) return
      p.suppliers.forEach(s => {
        if (s.name === g.supplier && s.price_position && s.price_position <= priceThreshold && s.price_position > 0 && s.spend > 0
            && (s.cohort_attr_count || 0) >= similarMinAttrs
            && (s.cohort_size || 0) >= minCohortSize) {
          goodItems.push({ pn: p.pn, desc: p.description || '', pp: s.price_position, spend: s.spend, uc: Number(s.unit_cost), bm: s.benchmark || 0, eau: parseFloat(s.eau) || 0, file_id: p.file_id || null, filename: p.filename || '', cohort_entries: s.cohort_entries || [], cohort_name: s.cohort_name || '', cohort_size: s.cohort_size || 0, cohort_median_uc: s.cohort_median_uc || 0, cohort_median_eau: s.cohort_median_eau || 0, material: p.material || '', method: p.method || '' })
        }
      })
    })
    goodItems.sort((a, b) => a.pp - b.pp)

    // Score negotiation strategies
    const strategies = scoreNegotiationStrategies(g, goodItems, parts)

    opps.push({
      type: 'negotiation',
      title: `${g.supplier} — ${g.items.length + g.validateItems.length} parts above expected price`,
      parts: uniqueParts,
      spendExposed: g.totalSpend,
      estSavings: g.totalOverspend,
      confidence: g.items.length >= 5 ? 'High' : g.items.length >= 2 ? 'Medium' : 'Low',
      quarantined: allQuarantined,
      quarantineReason: allQuarantined ? g.validateItems[0]?.quarantineReason : undefined,
      avgPP,
      strategies,
      detail: `Average price position ${avgPP.toFixed(2)}x. ${g.items.length} line items priced ${Math.round((avgPP - 1) * 100)}% above cohort benchmark on average.`,
      vizData: {
        supplierName: g.supplier,
        items: g.items.map(i => ({
          pn: i.part.pn, desc: i.part.description || '',
          pp: i.supplier.price_position, spend: i.supplier.spend,
          uc: Number(i.supplier.unit_cost), bm: i.supplier.benchmark,
          eau: parseFloat(i.supplier.eau) || 0, vol_exp: i.supplier.vol_exp || VOLUME_EXP,
          file_id: i.part.file_id || null, filename: i.part.filename || '',
          cohort_entries: i.supplier.cohort_entries || [],
          cohort_name: i.supplier.cohort_name || '',
          cohort_size: i.supplier.cohort_size || 0,
          cohort_median_uc: i.supplier.cohort_median_uc || 0,
          cohort_median_eau: i.supplier.cohort_median_eau || 0,
          material: i.part.material || '', method: i.part.method || '',
        })).slice(0, 12),
        goodItems: goodItems.slice(0, 8),
        validateItems: g.validateItems.map(i => ({
          pn: i.part.pn, desc: i.part.description || '',
          pp: i.supplier.price_position, spend: i.supplier.spend,
          uc: Number(i.supplier.unit_cost), bm: i.supplier.benchmark,
          eau: parseFloat(i.supplier.eau) || 0,
          quarantineReason: i.quarantineReason,
        })),
      },
    })
  })

  // 2. Sole-source risk
  const soleParts = parts.filter(p => {
    const pricedSuppliers = p.suppliers.filter(s => s.unit_cost > 0)
    return pricedSuppliers.length === 1 && p.total_spend > 2000
  })
  const soleBySupplier = {}
  soleParts.forEach(p => {
    const key = (p.suppliers.find(s => s.unit_cost > 0) || p.suppliers[0]).name
    if (!soleBySupplier[key]) soleBySupplier[key] = { supplier: key, parts: [], totalSpend: 0 }
    soleBySupplier[key].parts.push(p)
    soleBySupplier[key].totalSpend += p.total_spend
  })

  Object.values(soleBySupplier).sort((a, b) => b.totalSpend - a.totalSpend).forEach(g => {
    if (g.totalSpend < 5000) return
    opps.push({
      type: 'sole_source',
      title: `${g.supplier.substring(0, 35)} — ${g.parts.length} sole-sourced parts`,
      parts: g.parts,
      spendExposed: g.totalSpend,
      estSavings: g.totalSpend * 0.12,
      confidence: g.parts.length >= 5 ? 'High' : 'Medium',
      detail: `${formatEur(g.totalSpend)} spend at risk with no alternative supplier. Qualifying a second source could reduce risk and enable competitive pricing (typical 8–15% improvement).`,
      vizData: {
        supplierName: g.supplier, partCount: g.parts.length, totalSpend: g.totalSpend,
        topParts: g.parts.slice().sort((a, b) => b.total_spend - a.total_spend).slice(0, 8)
          .map(p => ({ pn: p.pn, desc: p.description || '', spend: p.total_spend })),
      },
    })
  })

  // 3. Part Family Optimization: groups of similar parts with price spread or supplier fragmentation
  const familyGroups = {}
  parts.forEach(p => {
    // Skip assemblies
    if (p.method && p.method.toLowerCase().includes('assembl')) return
    const attrs = [p.method, p.material, p.size, p.tolerance_band, p.complexity_tier].filter(v => !isUnknownValue(v))
    if (attrs.length < similarMinAttrs) return
    if (!p.suppliers.some(s => s.unit_cost > 0 && s.spend > 0)) return
    const famKey = attrs.join('|')
    if (!familyGroups[famKey]) familyGroups[famKey] = { parts: [], suppliers: new Set(), totalSpend: 0 }
    familyGroups[famKey].parts.push(p)
    familyGroups[famKey].totalSpend += p.total_spend
    p.suppliers.forEach(s => { if (s.name && s.unit_cost > 0) familyGroups[famKey].suppliers.add(s.name) })
  })

  Object.entries(familyGroups).sort((a, b) => b[1].totalSpend - a[1].totalSpend).forEach(([famKey, data]) => {
    const uniquePNs = [...new Set(data.parts.map(p => p.pn))]
    if (uniquePNs.length < 2) return

    // Collect all priced entries (exclude zero-cost lines)
    const allPrices = []
    data.parts.forEach(p => {
      p.suppliers.forEach(s => {
        if (s.unit_cost > 0 && s.spend > 0) {
          allPrices.push({ pn: p.pn, name: s.name, uc: s.unit_cost, spend: s.spend, eau: parseFloat(s.eau) || 0 })
        }
      })
    })
    if (allPrices.length < 2) return

    // Drop outliers (>8x median)
    const ucs = allPrices.map(e => e.uc).sort((a, b) => a - b)
    const med = ucs[Math.floor(ucs.length / 2)]
    const valid = allPrices.filter(e => e.uc <= med * 8 && e.uc >= med / 8)
    if (valid.length < 2) return

    // Only count parts that have valid pricing (not zero-cost or outlier-dropped)
    const pricedPNs = [...new Set(valid.map(e => e.pn))]
    if (pricedPNs.length < 2) return

    const best = valid.reduce((a, b) => a.uc < b.uc ? a : b)
    const worst = valid.reduce((a, b) => a.uc > b.uc ? a : b)
    const spread = worst.uc / best.uc
    const supplierCount = data.suppliers.size

    // Two levers: price alignment + consolidation
    // Phase 4d: price alignment uses regression should-cost when available.
    // A sibling is overpriced only vs its own should-cost, not vs the cheapest
    // different part in the family (audit/SAVINGS_AUDIT_RESULTS.md F1: cheapest-
    // sibling alignment measures dispersion, not overspend).
    let priceSavings = 0
    const famVolExp = data.parts[0]?._volExp || VOLUME_EXP
    const familyMargin = 0.1
    if (hasScModel) {
      // v2: each entry compared to its own should-cost
      valid.forEach(e => {
        // Find the should_cost for this entry from the enriched part's suppliers
        const part = data.parts.find(pp => pp.pn === e.pn)
        const sup = part?.suppliers?.find(s => s.name === e.name && s.unit_cost > 0)
        const sc = sup?.should_cost
        if (!sc || sc <= 0) return
        if (e.uc > sc * (1 + familyMargin)) {
          priceSavings += e.spend * (1 - sc * (1 + familyMargin) / e.uc)
        }
      })
    } else if (best.pn !== worst.pn && spread >= priceThreshold) {
      // Fallback: no regression model — family price alignment returns 0
      // (do NOT fall back to cheapest-sibling — that lever is proven to be noise;
      // see audit/SAVINGS_AUDIT_RESULTS.md F1)
      priceSavings = 0
    }
    // Consolidation analytics: defensible four-part economies-of-scale savings
    // bridge + ranked target vendors + fragmentation + single-source risk.
    // Replaces the legacy flat-8% guess. The bridge subsumes price alignment as
    // its first component, so it is always >= priceSavings.
    const consolidation = computeConsolidationAnalytics({
      parts: data.parts, valid, best, totalSpend: data.totalSpend,
      supplierCount, priceSavings, method: data.parts[0]?.method || '',
    }, params)
    // Use the bridge total once the family is fragmented enough to consolidate
    // (>= 4 suppliers); otherwise the price-alignment lever stands alone.
    const consolSavings = supplierCount >= 4 ? consolidation.bridge.total : priceSavings
    const savings = Math.max(priceSavings, consolSavings)
    if (savings < minSize) return

    const hasPriceGap = priceSavings > 0 && spread >= priceThreshold
    const hasConsolidation = supplierCount >= 4
    if (!hasPriceGap && !hasConsolidation) return

    // Build sub-signals for display
    const signals = []
    if (hasPriceGap) signals.push(spread >= 10 ? `${Math.round(spread)}× price range` : `${Math.round((spread - 1) * 100)}% price spread`)
    if (hasConsolidation) signals.push(`${supplierCount} suppliers`)

    const famLabel = humanizeFamilyLabel(data.parts[0])

    // Per-supplier spend breakdown
    const supSpend = {}
    data.parts.forEach(p => p.suppliers.forEach(s => { if (s.spend > 0) supSpend[s.name] = (supSpend[s.name] || 0) + s.spend }))
    const supBreakdown = Object.entries(supSpend).sort((a, b) => b[1] - a[1]).map(([name, spend]) => ({ name, spend }))

    // Per-part price summary for viz — only parts with valid pricing
    const partPrices = pricedPNs.map(pn => {
      const entries = valid.filter(e => e.pn === pn)
      const avgUC = entries.length > 0 ? entries.reduce((s, e) => s + e.uc, 0) / entries.length : 0
      const mainSupplier = entries.sort((a, b) => b.spend - a.spend)[0]?.name || ''
      const partObj = data.parts.find(pp => pp.pn === pn)
      return { pn, desc: partObj?.description || '', uc: avgUC, supplier: mainSupplier, spend: entries.reduce((s, e) => s + e.spend, 0) }
    }).filter(p => p.uc > 0).sort((a, b) => b.uc - a.uc)

    // Compute median and parts-above-median for better summaries
    const sortedPP = partPrices.length > 0 ? [...partPrices].sort((a, b) => a.uc - b.uc) : []
    const medUC = sortedPP.length > 0
      ? (sortedPP.length % 2 === 0 ? (sortedPP[sortedPP.length / 2 - 1].uc + sortedPP[sortedPP.length / 2].uc) / 2 : sortedPP[Math.floor(sortedPP.length / 2)].uc)
      : 0
    const aboveMedianParts = sortedPP.filter(p => p.uc > medUC)
    const aboveMedianSpend = aboveMedianParts.reduce((s, p) => s + p.spend, 0)

    opps.push({
      type: 'part_family',
      title: `${famLabel} — ${pricedPNs.length} parts, ${signals.join(', ')}`,
      parts: data.parts,
      spendExposed: data.totalSpend,
      estSavings: savings,
      confidence: pricedPNs.length >= 5 ? 'High' : pricedPNs.length >= 3 ? 'Medium' : 'Low',
      detail: `${pricedPNs.length} engineering-similar parts (${famLabel}) with ${formatEur(data.totalSpend)} total spend.`
        + (hasPriceGap ? ` Prices range \u20ac${best.uc.toFixed(2)}\u2013\u20ac${worst.uc.toFixed(2)} (${Math.round(spread)}\u00D7), median \u20ac${medUC.toFixed(2)}. ${aboveMedianParts.length} parts above median account for ${formatEur(aboveMedianSpend)} spend \u2014 aligning to best-in-family pricing is the primary lever.` : '')
        + (hasConsolidation ? ` Fragmented across ${supplierCount} suppliers (${consolidation.fragmentation.concentration}) \u2014 consolidating to ${consolidation.risk.targetSupplierCount === 1 ? 'one preferred vendor' : `${consolidation.risk.targetSupplierCount} preferred vendors`} (anchor: ${consolidation.targetVendors.anchor?.name || '\u2014'}) builds a ${formatEur(consolidation.bridge.total)} economies-of-scale case across price alignment, volume tiers, admin reduction${consolidation.bridge.toolingIntensive ? ', and tooling amortization' : ''}.` : ''),
      ppRatio: spread,
      vizData: {
        famLabel,
        partCount: pricedPNs.length,
        supplierCount,
        totalSpend: data.totalSpend,
        hasPriceGap,
        hasConsolidation,
        spread,
        best: { pn: best.pn, desc: data.parts.find(pp => pp.pn === best.pn)?.description || '', uc: best.uc, supplier: best.name },
        worst: { pn: worst.pn, desc: data.parts.find(pp => pp.pn === worst.pn)?.description || '', uc: worst.uc, supplier: worst.name },
        suppliers: supBreakdown.slice(0, 8),
        partPrices,
        consolidation,
      },
      consolidation,
    })
  })

  // ── Phase 2b: Global admin/tooling de-duplication pass ────────────────────
  // Each supplier exit is counted ONCE globally across all families. Sort families
  // by priceSavings descending; first family that claims a supplier wins its admin credit.
  const familyOpps = opps.filter(o => o.type === 'part_family' && o.consolidation)
  if (familyOpps.length > 0) {
    const globalEliminated = new Set()
    // Sort by priceSavings (the price_alignment component) descending
    const sorted = [...familyOpps].sort((a, b) => {
      const aPrice = a.consolidation?.bridge?.components?.find(c => c.key === 'price_alignment')?.amount || 0
      const bPrice = b.consolidation?.bridge?.components?.find(c => c.key === 'price_alignment')?.amount || 0
      return bPrice - aPrice
    })
    const adminCost = params.adminCostPerSupplier || ADMIN_COST_PER_SUPPLIER
    const toolingCost = params.toolingAmortPerSupplier || TOOLING_AMORT_PER_SUPPLIER

    sorted.forEach(opp => {
      const consol = opp.consolidation
      if (!consol) return
      const frag = consol.fragmentation
      const anchor = consol.targetVendors?.anchor?.name
      const tailNames = (frag?.tailSuppliers || []).map(s => s.name)

      let newAdmin = 0
      let newTooling = 0
      tailNames.forEach(name => {
        if (name === anchor) return
        if (!globalEliminated.has(name)) {
          globalEliminated.add(name)
          newAdmin += adminCost
          if (consol.bridge.toolingIntensive) newTooling += toolingCost
        }
      })

      // Update the bridge components in-place
      const adminComp = consol.bridge.components.find(c => c.key === 'admin')
      const toolingComp = consol.bridge.components.find(c => c.key === 'tooling')
      if (adminComp) {
        adminComp.amount = newAdmin
        adminComp.note = `First-claim global count — each supplier exit counted once across all families. ${newAdmin > 0 ? Math.round(newAdmin / adminCost) + ' new exits × ' + formatEur(adminCost) + '/yr.' : 'All tail suppliers already claimed by higher-priority families.'}`
      }
      if (toolingComp) {
        toolingComp.amount = newTooling
        if (consol.bridge.toolingIntensive && newTooling > 0) {
          toolingComp.note = `First-claim global count. ${Math.round(newTooling / toolingCost)} new tool-set consolidation(s) × ${formatEur(toolingCost)}/yr.`
        } else if (consol.bridge.toolingIntensive) {
          toolingComp.note = 'Tooling-intensive but all exits already claimed by higher-priority families.'
        }
      }

      // Recompute bridge total and opp estSavings
      const newTotal = consol.bridge.components.reduce((s, c) => s + c.amount, 0)
      consol.bridge.total = newTotal

      // Update the opp's estSavings: use bridge total if consolidation was the driver
      const priceSavings = consol.bridge.components.find(c => c.key === 'price_alignment')?.amount || 0
      const supplierCount = frag?.supplierCount || 0
      const consolSavings = supplierCount >= 4 ? newTotal : priceSavings
      opp.estSavings = Math.max(priceSavings, consolSavings)

      // Phase 2c: Cap family savings at familySavingsCap × family spend
      const familySpend = opp.spendExposed || 0
      const cap = familySavingsCap * familySpend
      if (opp.estSavings > cap && cap > 0) {
        opp.estSavings = cap
        opp.capped = true
        opp.cappedNote = `Savings capped at ${Math.round(familySavingsCap * 100)}% of family spend (${formatEur(cap)}).`
      }
    })
  }

  // 4. Quick wins
  parts.forEach(p => {
    const pricedSuppliers = p.suppliers.filter(s => s.unit_cost > 0)
    if (pricedSuppliers.length < 2) return
    const expensive = pricedSuppliers.filter(s => s.price_position && s.price_position > priceThreshold + 0.2)
    const cheaper = pricedSuppliers.filter(s => s.price_position && s.price_position < 1.0)
    if (expensive.length > 0 && cheaper.length > 0) {
      const best = cheaper.sort((a, b) => a.price_position - b.price_position)[0]
      const worst = expensive.sort((a, b) => b.price_position - a.price_position)[0]
      const savings = worst.spend * (1 - best.price_position / worst.price_position)
      if (savings > minSize) {
        // Outlier analysis: flag extreme ratios that likely indicate data issues
        // Grounded on real data: P75=2.8x, P90=6.7x across 2,865 multi-sourced parts
        const ppRatio = worst.price_position / Math.max(best.price_position, 0.01)
        const outlierFlags = []
        if (ppRatio > outlierThreshold * 3) {
          outlierFlags.push('Extreme price ratio (>' + Math.round(ppRatio) + 'x) — almost certainly a data error (e.g. unit-of-measure: box vs. each, or currency mismatch). Verify before acting.')
        } else if (ppRatio > outlierThreshold * 1.5) {
          outlierFlags.push('Very large price gap (' + ppRatio.toFixed(1) + 'x) — likely UoM or quantity-basis difference (e.g. per-piece vs. per-hundred). Confirm with supplier.')
        } else if (ppRatio > outlierThreshold) {
          outlierFlags.push('Unusual price gap (' + ppRatio.toFixed(1) + 'x) — above P75 of typical variation. Validate pricing basis before switching.')
        }
        opps.push({
          type: 'quick_win',
          title: `${p.pn}${p.description ? ' (' + p.description + ')' : ''} — switch from ${worst.name.substring(0, 25)} to ${best.name.substring(0, 25)}`,
          parts: [p],
          spendExposed: worst.spend,
          estSavings: savings,
          confidence: ppRatio > outlierThreshold * 1.5 ? 'Low' : ppRatio > outlierThreshold ? 'Medium' : 'High',
          detail: `${worst.name} charges ${worst.price_position.toFixed(2)}x benchmark while ${best.name} is at ${best.price_position.toFixed(2)}x for the same part. Switching saves ~${formatEur(savings)}/year.`,
          outlierFlags,
          ppRatio,
          quarantined: ppRatio >= validateRatio,
          quarantineReason: ppRatio >= validateRatio ? 'Price ratio ' + ppRatio.toFixed(1) + '\u00d7 \u2014 probable unit-of-measure / pack-size / revision mismatch. Verify with buyer before counting.' : undefined,
          vizData: {
            pn: p.pn, desc: p.description || '',
            current: { name: worst.name, pp: worst.price_position, spend: worst.spend },
            alternative: { name: best.name, pp: best.price_position, spend: best.spend },
          },
        })
      }
    }
  })

  // 5. Site price gap — the SAME part bought at different prices across sites.
  // Exposes siloed sourcing. Savings are VOLUME-ADJUSTED: a low-volume site
  // legitimately pays more per unit, so we only count the gap beyond what order
  // volume explains, measured against the best-achieved in-network price.
  parts.forEach(p => {
    let priced = (p.sites || []).filter(s => s.unit_cost > 0 && s.spend > 0)
    if (priced.length < 2) return
    // Drop implausible per-site prices (unit/currency mix-ups, one-off buys) that
    // would otherwise fabricate enormous gaps — keep within 8x of the median.
    const ucs = priced.map(s => s.unit_cost).sort((a, b) => a - b)
    const med = ucs[Math.floor(ucs.length / 2)]
    priced = priced.filter(s => s.unit_cost <= med * 8 && s.unit_cost >= med / 8)
    if (priced.length < 2) return

    const bestSite = priced.reduce((a, b) => (a.unit_cost < b.unit_cost ? a : b))
    const best = bestSite.unit_cost
    const bestEau = parseFloat(bestSite.eau) || 0
    const rawSpread = Math.max(...priced.map(s => s.unit_cost)) / best
    if (rawSpread < 1.2) return // <20% raw gap: not worth flagging

    // Volume-adjusted savings: each site is compared to the best price scaled to
    // that site's own volume (best site is the achievable floor). Only the excess
    // over the volume-explained premium counts.
    let savings = 0
    const siteVolExp = p._volExp || VOLUME_EXP
    priced.forEach(s => {
      const eau = parseFloat(s.eau) || 0
      const expected = best * volAdjust(bestEau, eau, siteVolExp)
      if (s.unit_cost > expected) savings += s.spend * (1 - expected / s.unit_cost)
    })
    if (savings < minSize) return

    const worst = priced.reduce((a, b) => (a.unit_cost > b.unit_cost ? a : b))
    const sitesSorted = priced.slice().sort((a, b) => b.unit_cost - a.unit_cost)
    const siteQuarantined = rawSpread >= validateRatio
    opps.push({
      type: 'site_gap',
      title: `${p.pn}${p.description ? ' (' + p.description + ')' : ''} — ${Math.round((rawSpread - 1) * 100)}% price gap across ${priced.length} sites`,
      parts: [p],
      sites: sitesSorted,
      spendExposed: p.total_spend,
      estSavings: savings,
      confidence: priced.length >= 4 ? 'High' : priced.length >= 3 ? 'Medium' : 'Low',
      quarantined: siteQuarantined,
      quarantineReason: siteQuarantined ? 'Price ratio ' + rawSpread.toFixed(1) + '\u00d7 \u2014 probable unit-of-measure / pack-size / revision mismatch. Verify with buyer before counting.' : undefined,
      detail: `${worst.site} pays €${worst.unit_cost.toFixed(2)}/unit while ${bestSite.site} achieves €${best.toFixed(2)} — a ${Math.round((rawSpread - 1) * 100)}% gap on the same part. Savings shown are volume-adjusted (only the portion beyond what order volume explains); pulling sites toward the best in-network price needs no requalification.`,
      vizData: { pn: p.pn, desc: p.description || '', best, bestSite: bestSite.site, spread: rawSpread, sites: sitesSorted },
    })
  })

  // 6. Supplier exit candidates — auto-surface suppliers whose book can be
  // redistributed across the existing base. The inverse of consolidation:
  // remove a supplier and push its parts out to the rest of each cohort.
  const exitIndex = buildCohortIndex(parts)
  const supTotals = {}
  parts.forEach(p => p.suppliers.forEach(s => {
    if (s.unit_cost > 0 && s.spend > 0) {
      if (!supTotals[s.name]) supTotals[s.name] = { spend: 0, parts: 0 }
      supTotals[s.name].spend += s.spend
      supTotals[s.name].parts += 1
    }
  }))
  const grandSpend = Object.values(supTotals).reduce((s, v) => s + v.spend, 0)
  let exitsSurfaced = 0
  Object.entries(supTotals).sort((a, b) => b[1].spend - a[1].spend).slice(0, 6).forEach(([name, t]) => {
    if (exitsSurfaced >= 3) return
    const share = grandSpend > 0 ? t.spend / grandSpend : 0
    const plan = computeSupplierExit(parts, name, params, exitIndex)
    const v = plan.vizData
    const redistPct = v.totalExposure > 0 ? v.redistributableSpend / v.totalExposure : 0
    // Surface only meaningful exits: a real redistribution path AND either
    // defensible savings or material supplier concentration to de-risk.
    if (redistPct < 0.5) return
    if (plan.estSavings < minSize && share < 0.12) return
    opps.push(plan)
    exitsSurfaced++
  })

  // Phase 8d: Surface top supplier negotiation opportunities
  // Cheaply estimate tierA+tierB value per supplier without full dossier
  const negoScores = {}
  parts.forEach(p => {
    ;(p.suppliers || []).forEach(s => {
      if (!s.name || !(s.unit_cost > 0) || !(s.spend > 0)) return
      const name = s.name
      if (!negoScores[name]) negoScores[name] = { tierAValue: 0, tierBValue: 0, spend: 0 }
      negoScores[name].spend += s.spend
      // Tier A: same-PN evidence (another supplier is cheaper)
      const others = p.suppliers.filter(os => os.name !== name && os.unit_cost > 0)
      if (others.length > 0) {
        const volExp = s.vol_exp || p._volExp || VOLUME_EXP
        const eau = parseFloat(s.eau) || 0
        const cheapest = others.reduce((a, b) => a.unit_cost < b.unit_cost ? a : b)
        const adjUc = cheapest.unit_cost * volAdjust(parseFloat(cheapest.eau) || 0, eau, volExp)
        if (s.unit_cost > adjUc * 1.05) {
          const gap = s.unit_cost / adjUc
          if (gap < validateRatio) { // not quarantined
            negoScores[name].tierAValue += s.spend * (1 - adjUc / s.unit_cost)
          }
        }
      }
      // Tier B: should-cost residual
      if (s.sc_residual != null && s.sc_sigma > 0 && s.sc_residual > sigmaThreshold * s.sc_sigma && s.should_cost > 0) {
        negoScores[name].tierBValue += s.spend * (1 - s.should_cost / s.unit_cost)
      }
    })
  })

  // Pick top 5 by combined value, insert as supplier_negotiation opps
  const negoRanked = Object.entries(negoScores)
    .filter(([, v]) => (v.tierAValue + v.tierBValue) > minSize)
    .sort((a, b) => (b[1].tierAValue + b[1].tierBValue) - (a[1].tierAValue + a[1].tierBValue))
    .slice(0, 5)

  negoRanked.forEach(([name, v]) => {
    const combined = v.tierAValue + v.tierBValue
    const askTarget = 0.9 * v.tierAValue + 0.35 * v.tierBValue
    opps.push({
      type: 'supplier_negotiation',
      title: `${name} — negotiate ${formatEur(askTarget)} across ${Math.round(v.spend > 0 ? combined / v.spend * 100 : 0)}% of book`,
      parts: parts.filter(p => p.suppliers.some(s => s.name === name && s.unit_cost > 0)),
      spendExposed: v.spend,
      estSavings: askTarget,
      confidence: v.tierAValue > 0 ? 'High' : 'Medium',
      evidenceTier: v.tierAValue > v.tierBValue ? 'A' : 'B',
      tierSplit: { B: v.tierBValue, A: v.tierAValue },
      effort: 2,
      detail: `${name}: ${formatEur(v.tierAValue)} observed evidence (Tier A) + ${formatEur(v.tierBValue)} model-inferred (Tier B). Target ask: ${formatEur(askTarget)}.`,
      vizData: { supplierName: name, tierAValue: v.tierAValue, tierBValue: v.tierBValue, totalSpend: v.spend },
    })
  })

  // Division preference: compute affinity and re-rank
  if (divisionPreference && siteToDivision) {
    opps.forEach(opp => {
      const { affinity, breakdown } = computeDivisionAffinity(opp, divisionPreference, siteToDivision)
      opp.divisionAffinity = affinity
      opp.divisionBreakdown = breakdown
      opp.effectiveSavings = opp.estSavings * (1 + DIVISION_BOOST * affinity)
      opp.divisionSavings = computeDivisionSavings(opp, siteToDivision)
    })
    opps.sort((a, b) => b.effectiveSavings - a.effectiveSavings)
  } else {
    opps.sort((a, b) => b.estSavings - a.estSavings)
    // Still compute breakdown for KPI bar (without preference boost)
    if (siteToDivision) {
      opps.forEach(opp => {
        opp.divisionAffinity = 0
        opp.divisionBreakdown = computeDivisionAffinity(opp, null, siteToDivision).breakdown
        opp.divisionSavings = computeDivisionSavings(opp, siteToDivision)
        opp.effectiveSavings = opp.estSavings
      })
    }
  }

  // Compute achievability for each opportunity
  opps.forEach(opp => {
    opp.achievability = computeAchievability(opp, outlierThreshold)
  })

  // Phase 6a: Tag every opportunity with evidenceTier
  const EFFORT_WEIGHT = { quick_win: 1, site_gap: 1, negotiation: 2, part_family: 4, supplier_exit: 5, sole_source: 3, supplier_negotiation: 2 }
  const TIER_WEIGHT = { A: 1, B: 0.6, C: 0.2 }

  opps.forEach(opp => {
    if (opp.type === 'quick_win' || opp.type === 'site_gap') {
      opp.evidenceTier = 'A'
    } else if (opp.type === 'negotiation') {
      opp.evidenceTier = 'B'
    } else if (opp.type === 'supplier_exit') {
      opp.evidenceTier = 'B'
    } else if (opp.type === 'sole_source') {
      opp.evidenceTier = 'C'
    } else if (opp.type === 'supplier_negotiation') {
      // Already set when pushed; preserve
      if (!opp.evidenceTier) opp.evidenceTier = 'B'
    } else if (opp.type === 'part_family') {
      // Split: priceSavings component is B, admin/tooling is C
      const consol = opp.consolidation
      if (consol) {
        const priceComp = consol.bridge.components.find(c => c.key === 'price_alignment')?.amount || 0
        const adminComp = consol.bridge.components.find(c => c.key === 'admin')?.amount || 0
        const toolingComp = consol.bridge.components.find(c => c.key === 'tooling')?.amount || 0
        const bAmount = Math.min(priceComp, opp.estSavings)
        const cAmount = Math.min(adminComp + toolingComp, opp.estSavings - bAmount)
        opp.evidenceTier = bAmount >= cAmount ? 'B' : 'C'
        opp.tierSplit = { B: bAmount, C: cAmount }
      } else {
        opp.evidenceTier = 'B'
      }
    } else {
      opp.evidenceTier = 'B'
    }

    // Phase 6c: effort-aware ranking score
    const tierW = TIER_WEIGHT[opp.evidenceTier] || 0.5
    const effort = EFFORT_WEIGHT[opp.type] || 3
    const ach = (opp.achievability?.score || 50) / 100
    opp.effortScore = (tierW * (opp.effectiveSavings || opp.estSavings) * ach) / effort
  })

  // Re-sort by effort-aware score (highest first)
  opps.sort((a, b) => (b.effortScore || 0) - (a.effortScore || 0))

  return opps
}

// ─── Supplier Exit (inverse of consolidation) ─────────────────────────────────
// Given a supplier to remove, decide where each of its parts should move within
// the EXISTING base. Because a supplier spans many cohorts, this is a one-to-many
// redistribution. Built directly off cohort membership (not the per-line
// cohort_entries, which the enricher only attaches when a line has eau>0) so it
// works for the many real lines that carry no annual-usage figure.

function median(arr) {
  if (!arr.length) return 0
  const s = [...arr].sort((a, b) => a - b)
  return s[Math.floor(s.length / 2)]
}

/**
 * Index every priced supplier line by sourcing cohort, with per-cohort medians.
 * Reused by both the auto-detect scan and computeSupplierExit so the cohort math
 * is identical to computePricePositions.
 */
function buildCohortIndex(parts) {
  const members = {}
  parts.forEach(p => {
    const coh = buildCohortKey(p)
    if (!coh) return
    p.suppliers.forEach(s => {
      if (!(s.unit_cost > 0)) return
      if (!members[coh]) members[coh] = []
      members[coh].push({
        supplier: s.name,
        unit_cost: s.unit_cost,
        eau: parseFloat(s.eau) || 0,
        spend: s.spend || 0,
        pn: p.pn || p.filename || '',
      })
    })
  })
  const stats = {}
  Object.entries(members).forEach(([coh, entries]) => {
    if (entries.length < 3) return
    const costs = entries.map(e => e.unit_cost)
    const eaus = entries.filter(e => e.eau > 0).map(e => e.eau)
    stats[coh] = { median_uc: median(costs), median_eau: eaus.length ? median(eaus) : 0 }
  })
  return { members, stats }
}

/**
 * Derive a "demonstrated capability" profile for every supplier from the parts
 * they already make — the input to the LLM sole-source capability analysis.
 * Returns { supplierName: { name, part_count, processes[], materials[],
 * tolerance_bands[], complexity_tiers[], axis_tiers[], size_bands[],
 * example_parts[] } }. All arrays sorted for deterministic payloads.
 */
export function buildSupplierCapabilityProfiles(parts, excludeName = null) {
  const prof = {}
  const add = (set, v) => { if (v && !isUnknownValue(v)) set.add(v) }
  parts.forEach(p => {
    p.suppliers.forEach(s => {
      if (!s.name || s.name === excludeName || !(s.unit_cost > 0)) return
      const pr = prof[s.name] || (prof[s.name] = {
        name: s.name, part_count: 0,
        _proc: new Set(), _mat: new Set(), _tol: new Set(), _cx: new Set(), _ax: new Set(), _sz: new Set(),
        example_parts: [],
      })
      pr.part_count++
      add(pr._proc, p.method); add(pr._mat, p.material); add(pr._tol, p.tolerance_band)
      add(pr._cx, p.complexity_tier); add(pr._ax, p.axis_tier); add(pr._sz, p.size)
      if (pr.example_parts.length < 4 && p.description) pr.example_parts.push({ pn: p.pn, desc: p.description })
    })
  })
  const out = {}
  Object.values(prof).forEach(pr => {
    out[pr.name] = {
      name: pr.name, part_count: pr.part_count,
      processes: [...pr._proc].sort(), materials: [...pr._mat].sort(),
      tolerance_bands: [...pr._tol].sort(), complexity_tiers: [...pr._cx].sort(),
      axis_tiers: [...pr._ax].sort(), size_bands: [...pr._sz].sort(),
      example_parts: pr.example_parts,
    }
  })
  return out
}

/**
 * Plan the exit of `supplierName`: split its parts into redistributable vs.
 * stuck, rank in-base alternatives per part (full fallback chain), assign each
 * to its top eligible (non-vetoed) receiver, and build the one-to-many landing
 * map plus a conservative→optimistic savings range.
 *
 * Pure function of (supplierName, params.vetoedReceivers): re-call with a larger
 * veto set to get the next-best plan — no internal mutation.
 *
 * @returns an opportunity object shaped like the other 5 types.
 */
export function computeSupplierExit(parts, supplierName, params = {}, _index = null) {
  const {
    negotiationTarget = 1.1,
    vetoedReceivers = [],
    qualCostPerPart = 3000, // €, FAI/PPAP + first-article per moved part (Phase 7c)
    receiverCapFactor = 2.5, // receiver's absorbed spend may not exceed this × current spend (Phase 7c)
  } = params
  const vetoed = new Set(vetoedReceivers)
  const { members, stats } = _index || buildCohortIndex(parts)

  // Build receiver current-spend totals for absorption cap (Phase 7c)
  const receiverCurrentSpend = {}
  parts.forEach(p => (p.suppliers || []).forEach(s => {
    if (s.name && s.name !== supplierName && s.unit_cost > 0 && s.spend > 0) {
      receiverCurrentSpend[s.name] = (receiverCurrentSpend[s.name] || 0) + s.spend
    }
  }))

  // 1. Gather the exiting supplier's parts and rank alternatives per part.
  // Phase 7a: classify alternatives by evidence tier.
  const affected = []
  parts.forEach(p => {
    const line = (p.suppliers || []).find(s => s.name === supplierName && s.unit_cost > 0)
    if (!line) return
    const uc = Number(line.unit_cost) || 0
    const eau = parseFloat(line.eau) || 0
    const spend = line.spend || (uc * eau) || 0
    const coh = buildCohortKey(p)
    const st = coh ? stats[coh] : null
    const exitVolExp = p._volExp || VOLUME_EXP
    const benchmark = st && st.median_uc > 0 ? st.median_uc * volAdjust(st.median_eau, eau, exitVolExp) : 0

    // Phase 7a: Build alternatives in four evidence classes
    const alternatives = []

    // Class 1: transacted_same_pn — another supplier already buys this exact PN
    ;(p.suppliers || []).forEach(s => {
      if (s.name === supplierName || !(s.unit_cost > 0)) return
      const projUc = s.unit_cost * volAdjust(parseFloat(s.eau) || 0, eau, exitVolExp)
      alternatives.push({
        name: s.name, evidence: 'transacted_same_pn', med_uc: s.unit_cost, projUc,
        typical_pp: benchmark > 0 ? projUc / benchmark : null,
        cohortSpend: s.spend || 0, partsInCohort: 1,
      })
    })

    // Class 2: quoted_same_pn — inforecord exists (from p.quotes, Phase 7b)
    ;(p.quotes || []).forEach(q => {
      if (q.supplier === supplierName || !(q.unit_cost > 0)) return
      // Don't duplicate if already in transacted
      if (alternatives.some(a => a.name === q.supplier && a.evidence === 'transacted_same_pn')) return
      const qEau = parseFloat(q.eau) || 0
      const projUc = q.unit_cost * (qEau > 0 ? volAdjust(qEau, eau, exitVolExp) : 1)
      alternatives.push({
        name: q.supplier, evidence: 'quoted_same_pn', med_uc: q.unit_cost, projUc,
        typical_pp: benchmark > 0 ? projUc / benchmark : null,
        cohortSpend: 0, partsInCohort: 0, needsQuote: false,
      })
    })

    // Class 3: cohort_inference — other suppliers in the same cohort
    const aggMap = {}
    if (coh && members[coh]) {
      members[coh].forEach(e => {
        if (e.supplier === supplierName) return
        // Skip suppliers already in class 1/2
        if (alternatives.some(a => a.name === e.supplier && (a.evidence === 'transacted_same_pn' || a.evidence === 'quoted_same_pn'))) return
        if (!aggMap[e.supplier]) aggMap[e.supplier] = { name: e.supplier, ucs: [], eaus: [], spend: 0 }
        aggMap[e.supplier].ucs.push(e.unit_cost)
        if (e.eau > 0) aggMap[e.supplier].eaus.push(e.eau)
        aggMap[e.supplier].spend += e.spend
      })
    }
    Object.values(aggMap).forEach(a => {
      const med = median(a.ucs)
      const medEau = a.eaus.length ? median(a.eaus) : 0
      const projUc = med * volAdjust(medEau, eau, exitVolExp)
      // If should-cost model available, use supplier's mean residual for projection
      const sup = (p.suppliers || []).find(s => s.name === a.name)
      const scResidual = sup?.sc_residual
      const shouldCost = (p.suppliers || []).find(s => s.name === supplierName)?.should_cost
      let scProjUc = null
      if (shouldCost > 0 && scResidual != null) {
        scProjUc = shouldCost * Math.exp(scResidual)
      }
      alternatives.push({
        name: a.name, evidence: 'cohort_inference', med_uc: med, projUc: scProjUc || projUc,
        typical_pp: benchmark > 0 ? (scProjUc || projUc) / benchmark : null,
        cohortSpend: a.spend, partsInCohort: a.ucs.length,
      })
    })

    // Class 4: capability_match — for stuck parts only (no class 1-3 alternatives)
    // Uses buildSupplierCapabilityProfiles to find capable suppliers
    if (alternatives.length === 0) {
      const profiles = buildSupplierCapabilityProfiles(parts, supplierName)
      Object.values(profiles).forEach(prof => {
        const method = (p.method || '').toLowerCase()
        const material = (p.material || '').toLowerCase()
        if (prof.processes.some(pr => pr.toLowerCase() === method) &&
            prof.materials.some(m => m.toLowerCase() === material)) {
          alternatives.push({
            name: prof.name, evidence: 'capability_match', med_uc: 0, projUc: 0,
            typical_pp: null, cohortSpend: 0, partsInCohort: prof.part_count || 0, needsQuote: true,
          })
        }
      })
    }

    // Sort: class 1 > 2 > 3 > 4, then by price/scale
    const evidenceOrder = { transacted_same_pn: 0, quoted_same_pn: 1, cohort_inference: 2, capability_match: 3 }
    alternatives.sort((x, y) => {
      const xo = evidenceOrder[x.evidence] ?? 9
      const yo = evidenceOrder[y.evidence] ?? 9
      if (xo !== yo) return xo - yo
      const xb = (x.typical_pp != null && x.typical_pp <= 1.0) ? 0 : 1
      const yb = (y.typical_pp != null && y.typical_pp <= 1.0) ? 0 : 1
      if (xb !== yb) return xb - yb
      if (y.cohortSpend !== x.cohortSpend) return y.cohortSpend - x.cohortSpend
      return (x.projUc || Infinity) - (y.projUc || Infinity)
    })

    affected.push({
      part: p, pn: p.pn, desc: p.description || '', family: p.family || '',
      cohort: coh || '', uc, eau, spend, benchmark,
      price_position: benchmark > 0 ? uc / benchmark : null, alternatives,
    })
  })

  const totalExposure = affected.reduce((s, a) => s + a.spend, 0)

  // 2. Assign each part to its top eligible alternative; cascade on veto and cap.
  const receiversMap = {}
  const stuck = [], vetoedOut = [], redistributable = []
  affected.forEach(a => {
    if (!a.alternatives.length) { stuck.push(a); return }
    const eligible = a.alternatives.filter(alt => !vetoed.has(alt.name) && !alt.needsQuote)
    if (!eligible.length) {
      // Check capability_match
      if (a.alternatives.some(alt => alt.needsQuote)) { stuck.push(a); return }
      vetoedOut.push(a)
      return
    }

    // Phase 7c: absorption cap — find first eligible receiver under cap
    let assigned = null
    for (const alt of eligible) {
      const currentAbsorbed = receiversMap[alt.name]?.spendAbsorbed || 0
      const maxAbsorb = (receiverCurrentSpend[alt.name] || 0) * receiverCapFactor
      if (maxAbsorb > 0 && currentAbsorbed + a.spend > maxAbsorb) continue // over cap
      assigned = alt
      break
    }
    if (!assigned) {
      // All eligible receivers are over cap — treat as stuck (soft veto)
      stuck.push(a)
      return
    }

    a.primary = assigned
    a.fallback = eligible.find(alt => alt !== assigned) || null
    redistributable.push(a)
    const r = receiversMap[assigned.name] || (receiversMap[assigned.name] = { name: assigned.name, partsAbsorbed: 0, spendAbsorbed: 0, projectedCost: 0, families: new Set() })
    const projSpend = a.uc > 0 ? a.spend * (assigned.projUc / a.uc) : a.spend
    r.partsAbsorbed++
    r.spendAbsorbed += a.spend
    r.projectedCost += Math.min(projSpend, a.spend)
    r.families.add(a.family || a.cohort)
  })
  const receivers = Object.values(receiversMap).map(r => ({
    name: r.name, partsAbsorbed: r.partsAbsorbed, spendAbsorbed: r.spendAbsorbed,
    projectedCost: r.projectedCost, familyCount: r.families.size,
  })).sort((x, y) => y.spendAbsorbed - x.spendAbsorbed)

  const redistributableSpend = redistributable.reduce((s, a) => s + a.spend, 0)
  const stuckSpend = stuck.reduce((s, a) => s + a.spend, 0)
  const vetoedOutSpend = vetoedOut.reduce((s, a) => s + a.spend, 0)

  // 3. Savings range. Floor = only parts currently above the negotiation target,
  // credited down to max(alternative price, target) — defensible. Ceiling =
  // every receiver hits its cohort-median price — optimistic.
  let floor = 0, ceiling = 0
  let movedQualCost = 0
  redistributable.forEach(a => {
    const projUc = a.primary.projUc
    const projSpend = a.uc > 0 ? a.spend * (projUc / a.uc) : a.spend
    if (projSpend < a.spend) ceiling += a.spend - projSpend
    if (a.benchmark > 0 && a.uc > 0) {
      const target = Math.max(projUc, a.benchmark * negotiationTarget)
      if (a.uc > target) floor += a.spend * (1 - target / a.uc)
    }
    // Phase 7c: switching cost — transacted_same_pn moves are free (already qualified)
    if (a.primary.evidence !== 'transacted_same_pn') {
      movedQualCost += qualCostPerPart
    }
  })

  // Phase 7c: net floor after qualification costs
  const netFloor = Math.max(0, floor - movedQualCost)
  const paybackMonths = movedQualCost > 0 && floor > 0 ? movedQualCost / (floor / 12) : 0

  const redistPct = totalExposure > 0 ? redistributableSpend / totalExposure : 0
  const confidence = redistPct >= 0.7 ? 'High' : redistPct >= 0.4 ? 'Medium' : 'Low'
  const familyCount = new Set(affected.map(a => a.family).filter(Boolean)).size
  const topReceivers = receivers.slice(0, 3).map(r => `${r.name.substring(0, 22)} (${r.partsAbsorbed})`).join(', ')

  const mapPart = a => ({
    pn: a.pn, desc: a.desc, spend: a.spend, uc: a.uc, eau: a.eau,
    cohort: a.cohort, price_position: a.price_position, benchmark: a.benchmark,
    file_id: a.part.file_id || null, filename: a.part.filename || '',
    method: a.part.method || '', material: a.part.material || '', size: a.part.size || '',
    tolerance_band: a.part.tolerance_band || '', complexity_tier: a.part.complexity_tier || '',
    axis_tier: a.part.axis_tier || '',
    primary: a.primary || null, fallback: a.fallback || null,
    alternatives: (a.alternatives || []).slice(0, 5).map(alt => ({
      name: alt.name, evidence: alt.evidence, med_uc: alt.med_uc, projUc: alt.projUc,
      typical_pp: alt.typical_pp, vetoed: vetoed.has(alt.name), needsQuote: alt.needsQuote || false,
    })),
  })

  return {
    type: 'supplier_exit',
    title: `Exit ${supplierName.substring(0, 32)} — move ${redistributable.length} of ${affected.length} parts to ${receivers.length} suppliers`,
    parts: affected.map(a => a.part),
    spendExposed: totalExposure,
    estSavings: netFloor,
    confidence,
    detail: `${supplierName} carries ${formatEur(totalExposure)} across ${affected.length} parts (${familyCount} families). `
      + `${redistributable.length} parts (${formatEur(redistributableSpend)}, ${Math.round(redistPct * 100)}%) can move to already-qualified suppliers`
      + (receivers.length ? ` — chiefly ${topReceivers}.` : '.')
      + (movedQualCost > 0 ? ` Net savings after ${formatEur(movedQualCost)} switching costs: ${formatEur(netFloor)}/yr (${paybackMonths.toFixed(0)}-month payback).` : '')
      + (stuck.length ? ` ${stuck.length} parts (${formatEur(stuckSpend)}) are sole-sourced in their cohort — no in-base alternative, so exit requires net-new qualification or retention.` : '')
      + (vetoedOut.length ? ` ${vetoedOut.length} parts (${formatEur(vetoedOutSpend)}) have no acceptable receiver left after vetoes.` : ''),
    vizData: {
      supplierName, receivers,
      redistributable: redistributable.map(mapPart),
      stuck: stuck.map(mapPart),
      vetoedOut: vetoedOut.map(mapPart),
      savingsFloor: floor, netFloor, savingsCeiling: ceiling,
      movedQualCost, paybackMonths, qualCostPerPart,
      totalExposure, redistributableSpend, stuckSpend, vetoedOutSpend,
      familyCount, partCount: affected.length,
      vetoedReceivers: [...vetoed],
    },
  }
}

// ─── Part-Family Consolidation Analytics (Layer A) ────────────────────────────
// The new intelligence the Consolidation Brief and RFQ both consume. Builds a
// defensible four-part economies-of-scale savings bridge (replacing the legacy
// flat-8% guess), ranks candidate vendors to win the family, and produces
// fragmentation + single-source-risk metrics. All client-side, fed off the same
// enriched part objects the rest of the supply-chain views use.

// Configurable, defensible cost constants — CITED in every output so the bridge
// is auditable. Override via computeOpportunities params. See PART_FAMILY_
// CONSOLIDATION_PLAN.md §4.3 (open decision: confirm the admin figure).
export const ADMIN_COST_PER_SUPPLIER = 5000    // €/yr fully-loaded cost to manage one active direct-materials supplier (PO processing, qualification upkeep, audits, AP transactions).
export const TOOLING_AMORT_PER_SUPPLIER = 3000 // €/yr duplicated tooling amortization avoided per eliminated supplier — tooling-intensive processes only.
export const DUAL_SOURCE_THRESHOLD = 150000    // € annual family spend above which a qualified second source is recommended for continuity.
export const TAIL_SHARE_THRESHOLD = 0.05       // suppliers below this share of family spend are tail/retirement candidates.

// Processes where dedicated per-supplier tooling is material — so consolidating
// avoids duplicating tool sets. Pure machining carries little dedicated tooling.
const TOOLING_INTENSIVE = ['cast', 'mold', 'mould', 'stamp', 'forge', 'die', 'injection', 'extrud', 'press']

/**
 * Compute consolidation analytics for a single part family.
 * @param {Object} family - { parts, valid, best, totalSpend, supplierCount, priceSavings, method }
 *   parts:     full enriched part objects (each with .suppliers carrying unit_cost/spend/eau/cohort_entries)
 *   valid:     priced, outlier-filtered entries [{ pn, name, uc, spend, eau }]
 *   best:      cheapest valid entry { pn, name, uc, eau }
 * @param {Object} params - tunables (admin/tooling/threshold overrides)
 * @returns {{ bridge, targetVendors, fragmentation, risk }}
 */
export function computeConsolidationAnalytics(family, params = {}) {
  const {
    adminCostPerSupplier = ADMIN_COST_PER_SUPPLIER,
    toolingAmortPerSupplier = TOOLING_AMORT_PER_SUPPLIER,
    dualSourceThreshold = DUAL_SOURCE_THRESHOLD,
    tailShareThreshold = TAIL_SHARE_THRESHOLD,
  } = params

  const parts = family.parts || []
  const valid = family.valid || []
  const best = family.best || null
  const bestUC = best?.uc || 0
  const totalSpend = family.totalSpend || 0
  const priceSavings = family.priceSavings || 0
  const method = (family.method || '').toLowerCase()
  const totalParts = new Set(valid.map(e => e.pn)).size

  // Per-supplier breakdown (spend, parts made, volume-weighted unit cost)
  const supStats = {}
  parts.forEach(p => (p.suppliers || []).forEach(s => {
    if (!(s.unit_cost > 0 && s.spend > 0)) return
    const st = supStats[s.name] || (supStats[s.name] = { name: s.name, spend: 0, pns: new Set(), ucWsum: 0, ucW: 0 })
    st.spend += s.spend
    st.pns.add(p.pn)
    st.ucWsum += s.unit_cost * s.spend
    st.ucW += s.spend
  }))
  const supBreakdown = Object.values(supStats)
    .map(st => ({ name: st.name, spend: st.spend }))
    .sort((a, b) => b.spend - a.spend)
  const supplierCount = family.supplierCount || supBreakdown.length

  // ── 1. Four-part economies-of-scale savings bridge ──────────────────────────
  // (a) Price alignment — already measured by the caller.
  // (b) Volume-tier lever — REMOVED (Phase 2a): summing EAU across different
  //     parts is not a defensible volume argument. 101/137 families hit the cap.
  // (c) Admin / overhead reduction — retire tail suppliers, keep an anchor (+ a
  //     dual source when fragmented). Per-supplier figure is configurable & cited.
  const targetSupplierCount = supplierCount <= 2 ? 1 : 2
  const eliminated = Math.max(0, supplierCount - targetSupplierCount)
  const adminSavings = eliminated * adminCostPerSupplier

  // (d) Tooling amortization — one tool set over full family volume vs. duplicated
  //     across vendors. Only for tooling-intensive processes (not pure machining).
  const toolingIntensive = TOOLING_INTENSIVE.some(t => method.includes(t))
  const toolingSavings = toolingIntensive ? eliminated * toolingAmortPerSupplier : 0

  const components = [
    {
      key: 'price_alignment',
      label: 'Price alignment to best-in-family',
      amount: priceSavings,
      note: `Above-best parts pulled to €${bestUC.toFixed(2)} (volume-adjusted).`,
    },
    {
      key: 'volume_tier',
      label: 'Volume-tier leverage (single-vendor award)',
      amount: 0,
      note: 'Removed: summing EAU across different parts is not a defensible volume argument.',
    },
    {
      key: 'admin',
      label: 'Administrative / overhead reduction',
      amount: adminSavings,
      note: `${eliminated} of ${supplierCount} suppliers retired (retain ${targetSupplierCount}) × ${formatEur(adminCostPerSupplier)}/supplier/yr fully-loaded management cost.`,
    },
    {
      key: 'tooling',
      label: 'Tooling amortization',
      amount: toolingSavings,
      note: toolingIntensive
        ? `${eliminated} duplicated tool set(s) avoided × ${formatEur(toolingAmortPerSupplier)}/yr amortized.`
        : 'Machined family — minimal dedicated per-supplier tooling; not applied.',
    },
  ]
  const total = components.reduce((s, c) => s + c.amount, 0)
  const bridge = {
    components,
    total,
    eliminated,
    targetSupplierCount,
    adminCostPerSupplier,
    toolingAmortPerSupplier,
    toolingIntensive,
  }

  // ── 2. Target-vendor selection (ranked candidates) ──────────────────────────
  const largestName = supBreakdown[0]?.name
  const candidates = Object.values(supStats).map(st => {
    const avgUc = st.ucW > 0 ? st.ucWsum / st.ucW : 0
    const ratio = bestUC > 0 ? avgUc / bestUC : 1
    const share = totalSpend > 0 ? st.spend / totalSpend : 0
    const partCount = st.pns.size
    const breadth = totalParts > 0 ? partCount / totalParts : 0
    const suppliesBest = best ? st.name === best.name : false

    let priceScore = 3
    if (ratio <= 1.05) priceScore = 40
    else if (ratio <= 1.2) priceScore = 30
    else if (ratio <= 1.5) priceScore = 18
    else if (ratio <= 2) priceScore = 8
    const shareScore = Math.min(25, Math.round(share * 25))
    const breadthScore = Math.min(25, Math.round(breadth * 25))
    const provenBonus = suppliesBest ? 10 : 0
    const score = Math.min(100, priceScore + shareScore + breadthScore + provenBonus)

    const reasons = []
    if (suppliesBest) reasons.push('holds the best-in-family price')
    if (ratio <= 1.1) reasons.push('already at/near best-in-family cost')
    else if (ratio <= 1.5) reasons.push(`~${Math.round((ratio - 1) * 100)}% above best`)
    else reasons.push(`${ratio.toFixed(1)}× best — weak on cost`)
    reasons.push(`${Math.round(share * 100)}% of family spend`)
    reasons.push(`makes ${partCount}/${totalParts} parts`)

    return {
      name: st.name, type: 'incumbent', spend: st.spend, share, partCount, breadth,
      avgUc, ratio, suppliesBest, score, rationale: reasons.join('; '),
    }
  }).sort((a, b) => b.score - a.score)

  candidates.forEach(c => {
    c.role = c.suppliesBest ? 'Incumbent best-in-family (lowest risk)'
      : c.name === largestName ? 'Largest incumbent (capacity & relationship)'
      : 'Incumbent'
  })
  const anchor = candidates[0] || null
  if (anchor && anchor === candidates[0]) anchor.recommended = true
  const alternates = candidates.slice(1)

  // Net-new alternates: suppliers seen on comparable cohort parts but not yet in
  // this family — candidates to open competitive tension (3–5 bidders is the norm).
  const familySet = new Set(Object.keys(supStats))
  const netNewMap = {}
  parts.forEach(p => (p.suppliers || []).forEach(s => (s.cohort_entries || []).forEach(e => {
    if (e.supplier && !familySet.has(e.supplier)) {
      const m = netNewMap[e.supplier] || (netNewMap[e.supplier] = { name: e.supplier, ucs: [], count: 0 })
      if (e.unit_cost > 0) m.ucs.push(e.unit_cost)
      m.count++
    }
  })))
  const netNew = Object.values(netNewMap).map(m => ({
    name: m.name, type: 'net_new',
    avgUc: m.ucs.length ? m.ucs.reduce((a, b) => a + b, 0) / m.ucs.length : 0,
    seen: m.count,
    rationale: `Seen on ${m.count} comparable cohort part${m.count !== 1 ? 's' : ''} — qualify to open competitive tension.`,
  })).sort((a, b) => (a.avgUc || Infinity) - (b.avgUc || Infinity)).slice(0, 5)

  const targetVendors = { candidates, anchor, alternates, netNew }

  // ── 3. Fragmentation + concentration metrics ────────────────────────────────
  const shares = supBreakdown.map(s => ({
    name: s.name, spend: s.spend, share: totalSpend > 0 ? s.spend / totalSpend : 0,
  }))
  const hhi = Math.round(shares.reduce((sum, s) => sum + Math.pow(s.share * 100, 2), 0))
  const concentration = hhi < 1500 ? 'Highly fragmented' : hhi < 2500 ? 'Moderately fragmented' : 'Concentrated'
  const tailSuppliers = shares.filter(s => s.share < tailShareThreshold)
  const tailSpend = tailSuppliers.reduce((sum, s) => sum + s.spend, 0)
  const fragmentation = {
    supplierCount, hhi, concentration, shares,
    tailSuppliers, tailSpend, tailShareThreshold,
  }

  // ── 4. Single-source risk register ──────────────────────────────────────────
  // Consolidation CREATES the very risk the sole_source play warns about.
  const anchorPartCount = anchor ? anchor.partCount : 0
  const qualGapPartCount = Math.max(0, totalParts - anchorPartCount)
  const qualGapShare = totalParts > 0 ? qualGapPartCount / totalParts : 0
  const dualSourceRecommended = totalSpend >= dualSourceThreshold
  const mitigations = []
  if (dualSourceRecommended) {
    mitigations.push(`Retain a qualified second source — family spend ${formatEur(totalSpend)} exceeds the ${formatEur(dualSourceThreshold)} dual-source threshold. Keep ~20–30% of volume dual-sourced for continuity.`)
  }
  if (qualGapPartCount > 0) {
    mitigations.push(`${qualGapPartCount} of ${totalParts} parts are net-new to the anchor (${anchor?.name || '—'}) — require FAI/PPAP and first-article qualification before transfer.`)
  }
  mitigations.push('Phase tail suppliers out only after anchor qualification is signed off.')
  mitigations.push('Hold 4–8 weeks of safety stock through the transition to cover ramp risk.')
  const risk = {
    continuityExposure: totalSpend,
    dualSourceRecommended,
    dualSourceThreshold,
    anchorName: anchor?.name || null,
    anchorProven: anchor?.suppliesBest || false,
    qualGapPartCount,
    qualGapShare,
    targetSupplierCount,
    mitigations,
  }

  return { bridge, targetVendors, fragmentation, risk }
}

/**
 * Achievability scoring (0–100) and label for an opportunity.
 * Factors:
 *   - Cohort quality (attribute count): 2 attrs = weaker benchmark, 3+ = solid
 *   - Cohort depth (members): more data points = more reliable
 *   - Existing cheaper alternative (quick_win = very actionable)
 *   - Site gap = highest actionability (same part, no requalification)
 *   - PP magnitude: extreme values (>5x) may signal data issues
 *   - Confidence already computed
 */
function computeAchievability(opp, outlierThreshold = 3) {
  let score = 50 // base

  if (opp.type === 'site_gap') {
    // Most actionable — same part, same supplier qualified, just redirect PO
    score = 90
    if (opp.confidence === 'High') score = 95
  } else if (opp.type === 'quick_win') {
    // Cheaper source already qualified — redirect PO
    score = 85
    // Outlier penalty: extreme ratios reduce achievability significantly
    // Based on real data: P75=2.8x, P90=6.7x, P95=13x
    if (opp.ppRatio > outlierThreshold * 3) score = 20 // almost certainly a data error
    else if (opp.ppRatio > outlierThreshold * 1.5) score = 35 // likely UoM mismatch
    else if (opp.ppRatio > outlierThreshold) score = 55 // unusual gap, needs validation
  } else if (opp.type === 'negotiation') {
    // Benchmark-based — quality depends on cohort
    score = 60
    // Cohort attribute count: average across parts
    const attrCounts = opp.parts.map(p => p.cohort_attr_count || 0).filter(c => c > 0)
    const avgAttrs = attrCounts.length > 0 ? attrCounts.reduce((s, c) => s + c, 0) / attrCounts.length : 0
    if (avgAttrs >= 4) score += 20
    else if (avgAttrs >= 3) score += 12
    else if (avgAttrs >= 2) score += 0 // stays at base
    // Cohort depth
    const cohortSizes = opp.parts.flatMap(p => p.suppliers.filter(s => s.cohort_size).map(s => s.cohort_size))
    const avgCohort = cohortSizes.length > 0 ? cohortSizes.reduce((s, c) => s + c, 0) / cohortSizes.length : 0
    if (avgCohort >= 10) score += 8
    else if (avgCohort >= 5) score += 4
    // Number of affected line items (more = better leverage)
    if (opp.parts.length >= 5) score += 5
    // PP sanity: very extreme values suggest data quality issues
    if (opp.avgPP > 5) score -= 15
    else if (opp.avgPP > 3) score -= 5
    // Dispersion penalty: wide cohort price spreads weaken comparability
    const disps = opp.parts.flatMap(p => p.suppliers.map(s => s.cohort_dispersion).filter(Boolean))
    const maxDisp = disps.length ? Math.max(...disps) : 0
    if (maxDisp > 2.0) score -= 10
  } else if (opp.type === 'sole_source') {
    // Hardest to execute — need new qualification
    score = 35
    if (opp.parts.length >= 5) score += 10
    if (opp.confidence === 'High') score += 10
  } else if (opp.type === 'part_family') {
    // Combined: price alignment + consolidation
    score = 55
    if (opp.parts.length >= 5) score += 10
    if (opp.confidence === 'High') score += 8
    // Price gap signal boosts score (measured savings vs estimated)
    if (opp.vizData?.hasPriceGap) score += 10
    // More suppliers = more consolidation opportunity
    if (opp.vizData?.supplierCount >= 6) score += 5
    // Outlier penalty for extreme spreads
    if (opp.ppRatio > outlierThreshold * 3) score = 25
    else if (opp.ppRatio > outlierThreshold * 1.5) score -= 15
    else if (opp.ppRatio > outlierThreshold) score -= 5
  } else if (opp.type === 'supplier_exit') {
    // Driven by how much of the book can move to already-qualified suppliers.
    const v = opp.vizData || {}
    const redistPct = v.totalExposure > 0 ? v.redistributableSpend / v.totalExposure : 0
    score = 30 + Math.round(redistPct * 50) // 30–80 by redistributable share
    // Reward when primary receivers are already at/below benchmark (proven cheaper)
    const recv = v.receivers || []
    if (recv.length >= 2) score += 5
    // Penalize re-concentration: one receiver swallowing most of the moved spend
    const topShare = recv.length && v.redistributableSpend > 0 ? recv[0].spendAbsorbed / v.redistributableSpend : 0
    if (topShare > 0.6) score -= 12
    // Penalize large stuck (sole-source) exposure — the hard part of an exit
    const stuckPct = v.totalExposure > 0 ? v.stuckSpend / v.totalExposure : 0
    if (stuckPct > 0.4) score -= 12
    else if (stuckPct > 0.25) score -= 6
  }

  score = Math.max(10, Math.min(100, score))

  let label, color, tip
  if (score >= 80) {
    label = 'High'
    color = 'green'
    tip = 'Straightforward to execute — strong data support, clear action path'
  } else if (score >= 60) {
    label = 'Medium'
    color = 'amber'
    tip = 'Achievable with effort — benchmark is directional, validate with quotes'
  } else if (score >= 40) {
    label = 'Low'
    color = 'red'
    tip = 'Requires significant effort — coarse benchmark or new qualification needed'
  } else {
    label = 'Exploratory'
    color = 'gray'
    tip = 'Directional signal only — validate data before acting'
  }

  // Warnings for cohort-quality issues
  const warnings = []
  if (opp.type === 'negotiation') {
    const attrCounts = opp.parts.map(p => p.cohort_attr_count || 0).filter(c => c > 0)
    const avgAttrs = attrCounts.length > 0 ? attrCounts.reduce((s, c) => s + c, 0) / attrCounts.length : 0
    if (avgAttrs < 3) {
      warnings.push('Benchmark based on broad cohort (2 attributes) — less precise than narrow comparisons')
    }
    const cohortSizes = opp.parts.flatMap(p => p.suppliers.filter(s => s.cohort_size).map(s => s.cohort_size))
    const avgCohort = cohortSizes.length > 0 ? cohortSizes.reduce((s, c) => s + c, 0) / cohortSizes.length : 0
    if (avgCohort < 5) {
      warnings.push('Small cohort — fewer comparable transactions to benchmark against')
    }
    if (opp.avgPP && opp.avgPP > 5) {
      warnings.push('Extreme price deviation — verify unit/currency consistency before acting')
    }
    const dispsW = opp.parts.flatMap(p => p.suppliers.map(s => s.cohort_dispersion).filter(Boolean))
    const maxDispW = dispsW.length ? Math.max(...dispsW) : 0
    if (maxDispW > 2.0) {
      warnings.push(`Wide cohort price spread (P75/P25 = ${maxDispW.toFixed(1)}x) — comparability is weaker; validate with quotes before anchoring`)
    }
  }
  if (opp.type === 'sole_source') {
    warnings.push('Requires qualifying a new supplier — lead time 3–12 months')
  }
  if (opp.type === 'supplier_exit') {
    const v = opp.vizData || {}
    if (v.stuckSpend > 0) {
      warnings.push(`${formatEur(v.stuckSpend)} is sole-sourced in its cohort — those parts need net-new qualification or retention before a full exit`)
    }
    const recv = v.receivers || []
    const topShare = recv.length && v.redistributableSpend > 0 ? recv[0].spendAbsorbed / v.redistributableSpend : 0
    if (topShare > 0.6 && recv[0]) {
      warnings.push(`${recv[0].name} would absorb ${Math.round(topShare * 100)}% of the moved spend — veto it to spread risk and see the next-best plan`)
    }
    if ((v.vetoedOutSpend || 0) > 0) {
      warnings.push(`${formatEur(v.vetoedOutSpend)} has no acceptable receiver left after current vetoes`)
    }
  }
  if (opp.type === 'part_family') {
    if (opp.vizData?.hasPriceGap) {
      warnings.push('Cross-PN price comparison — parts share engineering attributes but verify functional interchangeability')
    }
    if (opp.ppRatio > outlierThreshold * 1.5) {
      warnings.push('Very large spread (' + opp.ppRatio.toFixed(1) + 'x) — likely UoM or packaging difference between part numbers')
    }
    if (opp.vizData?.hasConsolidation && opp.vizData?.supplierCount >= 6) {
      warnings.push('High fragmentation (' + opp.vizData.supplierCount + ' suppliers) — consolidation requires procurement alignment across sites')
    }
  }
  // Outlier warnings for any opp type with extreme price ratios
  if (opp.outlierFlags && opp.outlierFlags.length > 0) {
    opp.outlierFlags.forEach(f => warnings.push(f))
  }
  if (opp.type === 'quick_win' || opp.type === 'site_gap') {
    // Check for extreme PP values even if ppRatio wasn't computed (site_gap)
    const allPPs = opp.parts.flatMap(p => p.suppliers.map(s => s.price_position).filter(Boolean))
    const maxPP = allPPs.length > 0 ? Math.max(...allPPs) : 0
    if (maxPP > outlierThreshold * 1.5 && !opp.outlierFlags?.length) {
      warnings.push('Price position >' + Math.round(maxPP) + 'x — possible data entry error (UoM, currency, or quantity basis mismatch)')
    }
  }

  return { score, label, color, tip, warnings }
}

/**
 * Compute deduplicated total savings across opportunities.
 * The same (pn, supplier) combo may appear in multiple opp types — we only
 * count the highest single savings claim per combo to avoid inflation.
 * For family-level opps (part_family), savings are distributed pro-rata.
 */
export function deduplicatedSavings(opps) {
  // Map: "pn||supplier" → max savings attributed to that key
  const keyMax = {}

  opps.forEach(opp => {
    // Quarantined opportunities are excluded from headline totals (Phase 1a)
    if (opp.quarantined) return

    if (opp.type === 'negotiation') {
      // Each item in vizData has specific per-part savings — use the floored target
      // (same basis as the opportunity cards) so headline === Σ cards.
      const items = opp.vizData?.items || []
      const nt = opp._negotiationTarget || 1.1
      const itemSaving = (i) => {
        const uc = Number(i.uc) || 0
        const rawTarget = (i.bm || 0) * nt
        const realPeers = (i.cohort_entries || []).filter(e => e.unit_cost > 0)
        const cheapest = realPeers.length
          ? realPeers.reduce((a, b) => (a.unit_cost < b.unit_cost ? a : b))
          : null
        const itemExp = i.vol_exp || VOLUME_EXP
        const peerFloor = cheapest ? cheapest.unit_cost * volAdjust(cheapest.eau || 0, parseFloat(i.eau) || 0, itemExp) : rawTarget
        const target = Math.max(rawTarget, peerFloor)
        if (!uc || uc <= target) return 0
        return i.spend * (1 - target / uc)
      }
      const totalOverspend = items.reduce((s, i) => s + itemSaving(i), 0) || opp.estSavings
      items.forEach(item => {
        const saving = itemSaving(item)
        const key = `${item.pn}||${opp.vizData?.supplierName || ''}`
        keyMax[key] = Math.max(keyMax[key] || 0, saving)
      })
      // If vizData items are truncated (slice(0,12)), add remaining proportionally
      if (items.length > 0 && opp.estSavings > totalOverspend * 1.01) {
        const residual = opp.estSavings - totalOverspend
        const rKey = `__residual_nego_${opp.vizData?.supplierName || ''}`
        keyMax[rKey] = Math.max(keyMax[rKey] || 0, residual)
      }
    } else if (opp.type === 'sole_source') {
      // Distributed across parts
      const parts = opp.parts || []
      parts.forEach(p => {
        const sup = p.suppliers?.[0]?.name || ''
        const share = parts.length > 0 ? (p.total_spend / (opp.spendExposed || 1)) * opp.estSavings : 0
        const key = `${p.pn}||${sup}`
        keyMax[key] = Math.max(keyMax[key] || 0, share)
      })
    } else if (opp.type === 'part_family') {
      // Pro-rata across all parts×suppliers in the family
      const parts = opp.parts || []
      parts.forEach(p => {
        (p.suppliers || []).forEach(s => {
          const share = (opp.spendExposed > 0) ? (s.spend / opp.spendExposed) * opp.estSavings : 0
          const key = `${p.pn}||${s.name}`
          keyMax[key] = Math.max(keyMax[key] || 0, share)
        })
      })
    } else if (opp.type === 'quick_win') {
      // Attributed to the expensive supplier on this part
      const pn = opp.vizData?.pn || opp.parts?.[0]?.pn || ''
      const sup = opp.vizData?.current?.name || ''
      const key = `${pn}||${sup}`
      keyMax[key] = Math.max(keyMax[key] || 0, opp.estSavings)
    } else if (opp.type === 'site_gap') {
      // Attribute savings per site line to that site's dominant supplier (Phase 1b).
      // This makes site_gap claims collide with negotiation claims on the same pn+supplier,
      // preventing double-counting.
      const pn = opp.vizData?.pn || opp.parts?.[0]?.pn || ''
      const sites = opp.sites || opp.vizData?.sites || []
      const bestSiteUc = opp.vizData?.best || 0
      if (sites.length > 0 && bestSiteUc > 0) {
        sites.forEach(s => {
          const eau = parseFloat(s.eau) || 0
          const expected = bestSiteUc * volAdjust(parseFloat((sites.find(st => st.unit_cost === bestSiteUc) || {}).eau) || 0, eau)
          const siteSaving = s.unit_cost > expected ? s.spend * (1 - expected / s.unit_cost) : 0
          if (siteSaving > 0) {
            const sup = (s.suppliers && s.suppliers[0]) || '__site_gap'
            const key = `${pn}||${sup}`
            keyMax[key] = Math.max(keyMax[key] || 0, siteSaving)
          }
        })
      } else {
        // Fallback: old single-key approach
        const key = `${pn}||__site_gap`
        keyMax[key] = Math.max(keyMax[key] || 0, opp.estSavings)
      }
    } else if (opp.type === 'supplier_exit') {
      // Attribute the floor saving per moved part to (pn, exiting supplier) so it
      // collides with any negotiation/family claim on the same part rather than
      // double-counting. Distributed pro-rata by spend across redistributable parts.
      const v = opp.vizData || {}
      const sup = v.supplierName || ''
      const base = v.redistributableSpend || 0
      ;(v.redistributable || []).forEach(rp => {
        const share = base > 0 ? (rp.spend / base) * opp.estSavings : 0
        const key = `${rp.pn}||${sup}`
        keyMax[key] = Math.max(keyMax[key] || 0, share)
      })
    } else if (opp.type === 'supplier_negotiation') {
      // Phase 8d: key by pn||supplier — collides with negotiation/quick_win claims
      const sup = opp.vizData?.supplierName || ''
      const parts = opp.parts || []
      parts.forEach(p => {
        const sLine = (p.suppliers || []).find(s => s.name === sup && s.spend > 0)
        if (!sLine) return
        const share = opp.spendExposed > 0 ? (sLine.spend / opp.spendExposed) * opp.estSavings : 0
        const key = `${p.pn}||${sup}`
        keyMax[key] = Math.max(keyMax[key] || 0, share)
      })
    } else {
      // Unknown type — count fully
      const key = `__other_${opp.title || ''}`
      keyMax[key] = Math.max(keyMax[key] || 0, opp.estSavings || 0)
    }
  })

  const total = Object.values(keyMax).reduce((s, v) => s + v, 0)

  // Phase 6: compute byTier breakdown (approximate — allocates the dedup total
  // by the proportion each tier contributes in the raw opps)
  const rawByTier = { A: 0, B: 0, C: 0 }
  opps.forEach(opp => {
    if (opp.quarantined) return
    const tier = opp.evidenceTier || 'B'
    if (opp.tierSplit) {
      rawByTier.B += opp.tierSplit.B || 0
      rawByTier.C += opp.tierSplit.C || 0
    } else {
      rawByTier[tier] = (rawByTier[tier] || 0) + opp.estSavings
    }
  })
  const rawTotal = rawByTier.A + rawByTier.B + rawByTier.C
  const byTier = rawTotal > 0 ? {
    A: total * (rawByTier.A / rawTotal),
    B: total * (rawByTier.B / rawTotal),
    C: total * (rawByTier.C / rawTotal),
    headline: total * ((rawByTier.A + rawByTier.B) / rawTotal),
  } : { A: 0, B: 0, C: 0, headline: total }

  return { total, byTier }
}

/**
 * Step-by-step negotiation playbook for an opportunity, tailored by type.
 * Returns { steps: string[] (may contain <strong>/<span class="step-data">) }.
 */
export function generateActionPlan(opp) {
  const d = opp.vizData || {}
    if (opp.type === 'negotiation') {
    const items = d.items || []
    const top3 = items.slice(0, 3)
    const highPP = items.filter(i => i.pp > 1.5)
    const steps = []

    // Strategy recommendation (if scored)
    if (opp.strategies && opp.strategies.length > 0) {
      const top = opp.strategies[0]
      const viable = opp.strategies.filter(s => s.score >= 50)
      const recList = viable.slice(1, 4).map(s => `${s.icon} <strong>${s.name}</strong> (${s.score}/100)`).join(' &nbsp;\u00B7&nbsp; ')
      const notRec = opp.strategies.filter(s => s.score < 30)
      const notRecStr = notRec.length > 0 ? ` Not recommended: ${notRec.map(s => s.name).join(', ')}.` : ''
      steps.push(`<strong>Recommended strategy:</strong> ${top.icon} <span class="step-data">${top.name}</span> (score ${top.score}/100). ${top.desc}${viable.length > 1 ? '<br/><span style="font-size:0.9em;color:#6b7280">Also viable: ' + recList + '</span>' : ''}${notRecStr ? '<br/><span style="font-size:0.85em;color:#9ca3af">' + notRecStr + '</span>' : ''}`)
    }

    steps.push(`<strong>Map the exposure:</strong> ${items.length} parts from <span class="step-data">${d.supplierName}</span> are priced above their cohort benchmark. Average position <span class="step-data">${(opp.avgPP || 0).toFixed(2)}x</span> (${Math.round(((opp.avgPP || 1) - 1) * 100)}% above market). Total spend at risk: ${formatEur(opp.spendExposed)}.`)
    if (top3.length > 0) {
      const nt = opp._negotiationTarget || 1.1
      const topList = top3.map(i => `${i.desc ? i.pn + ' (' + i.desc + ')' : i.pn}: ${i.pp.toFixed(2)}x → ${formatEur(i.spend * (1 - nt / i.pp))} overspend`).join('; ')
      steps.push(`<strong>Prioritize highest-overspend parts:</strong> ${topList}.`)
    }
    if (highPP.length > 0) {
      steps.push(`<strong>Flag critical outliers:</strong> ${highPP.length} part${highPP.length > 1 ? 's' : ''} exceed${highPP.length === 1 ? 's' : ''} 1.5x benchmark — these are pricing anomalies, not minor variances.`)
    }
    const goodItems = d.goodItems || []
    if (goodItems.length > 0) {
      const goodSpend = goodItems.reduce((s, g) => s + g.spend, 0)
      steps.push(`<strong>Acknowledge competitive items:</strong> ${goodItems.length} other parts from this supplier are priced at or below benchmark (<span class="step-data">${formatEur(goodSpend)}</span> competitive spend). Use this: "We recognize you're competitive on ${goodItems.length} items — we just need the same discipline on these ${items.length} outliers."`)
    }
    const topItem = top3[0]
    const nt2 = opp._negotiationTarget || 1.1
    const ntPct = Math.round((nt2 - 1) * 100)
    let targetUC = null
    if (topItem?.bm) {
      const rawT = topItem.bm * nt2
      const peers = (topItem.cohort_entries || []).filter(e => e.unit_cost > 0)
      const cheap = peers.length ? peers.reduce((a, b) => (a.unit_cost < b.unit_cost ? a : b)) : null
      const floor = cheap ? cheap.unit_cost * volAdjust(cheap.eau || 0, parseFloat(topItem.eau) || 0) : rawT
      targetUC = Math.max(rawT, floor).toFixed(2)
    }
    steps.push(`<strong>Set your target:</strong> Aim for <span class="step-data">${nt2}x benchmark</span> (${ntPct}% premium max)${topItem ? `. Example: ${topItem.desc ? topItem.pn + ' (' + topItem.desc + ')' : topItem.pn} target €${targetUC} vs current €${topItem.uc.toFixed(2)}` : ''}.`)
    steps.push(`<strong>Challenge the supplier:</strong> "Our benchmarking shows ${items.length} line items priced ${Math.round(((opp.avgPP || 1) - 1) * 100)}% above comparable parts. We have data on each one. Please review and provide revised pricing within 2 weeks."`)
    steps.push(`<strong>Leverage total volume:</strong> Bundle all ${items.length} affected parts — total spend <span class="step-data">${formatEur(opp.spendExposed)}</span>. "If you align pricing, we commit volume. If not, we issue RFQs to qualified alternates."`)
    steps.push(`<strong>Walk-away position:</strong> Anything above <span class="step-data">1.3x benchmark</span> is non-negotiable. Set 2-week deadline, then competitive RFQ.`)
    return steps
  }
  if (opp.type === 'quick_win') {
    const savePct = Math.round((1 - d.alternative.pp / d.current.pp) * 100)
    const qwLabel = d.desc ? `${d.pn} (${d.desc})` : d.pn
    return [
      `<strong>Verify qualification:</strong> Confirm ${d.alternative.name} is already approved for part ${qwLabel}.`,
      `<strong>Get a fresh quote:</strong> Request updated pricing from ${d.alternative.name} — historical position <span class="step-data">${d.alternative.pp.toFixed(2)}x benchmark</span>.`,
      `<strong>Present to incumbent:</strong> Show ${d.current.name} the competitive quote. "We have a qualified alternative at ${savePct}% less — can you match?"`,
      `<strong>Decision gate:</strong> If ${d.current.name} matches → stay (saves switching cost). If not → execute the switch.`,
      `<strong>Switch if needed:</strong> Redirect the PO to ${d.alternative.name}. Potential annual savings: <span class="step-data">${formatRange(opp.estSavings)}</span>.`,
    ]
  }
  if (opp.type === 'sole_source') {
    const topParts = (d.topParts || []).slice(0, 3).map(p => p.desc ? `${p.pn} (${p.desc})` : p.pn).join(', ')
    const avgSpend = d.partCount ? formatEur(d.totalSpend / d.partCount) : '—'
    return [
      `<strong>Identify alternates:</strong> Research 2–3 suppliers for your top parts (${topParts}). Start with suppliers already in your network for adjacent families.`,
      `<strong>Issue exploratory RFQ:</strong> Request budgetary quotes for the highest-spend parts — just price signals, no full qualification yet.`,
      `<strong>Use as leverage:</strong> Share budgetary quotes with ${d.supplierName}: "We're evaluating options for risk mitigation. Can you sharpen pricing to justify staying single-source?"`,
      `<strong>Negotiate a volume commitment:</strong> If no viable alternates, negotiate a 2–3 year agreement with ${d.supplierName} for an 8–12% reduction.`,
      `<strong>Invest in qualification:</strong> For parts over <span class="step-data">${avgSpend}</span> annual spend, qualify a second source. Payback typically < 18 months.`,
      `<strong>Set a dual-source target:</strong> At least 2 qualified suppliers for all parts above €10K annual spend within 12 months.`,
    ]
  }
  if (opp.type === 'part_family') {
    const top2 = (d.suppliers || []).slice(0, 2).map(s => s.name).join(' and ')
    const partPrices = d.partPrices || []
    const bestUC = d.best?.uc || 0
    // Identify top 3 parts by savings potential (gap vs best-in-family)
    const ranked = partPrices.filter(p => p.uc > bestUC).map(p => ({ ...p, gap: p.uc - bestUC, savingsPot: p.spend * (1 - bestUC / p.uc) })).sort((a, b) => b.savingsPot - a.savingsPot)
    const top3 = ranked.slice(0, 3)

    // Compute similarity scores vs the reference (best-priced) part
    const refPart = (opp.parts || []).find(p => p.pn === d.best?.pn)
    let highSimCount = 0, lowSimCount = 0
    if (refPart) {
      (opp.parts || []).forEach(p => {
        if (p === refPart) return
        const sim = computeSimilarity(refPart, p)
        if (sim.pct >= 70) highSimCount++
        else if (sim.pct < 40) lowSimCount++
      })
    }

    const steps = []
    steps.push(`<strong>Map the family:</strong> ${d.partCount} engineering-similar parts across ${d.supplierCount} suppliers \u2014 total spend <span class="step-data">${formatEur(d.totalSpend)}</span>. Prices range \u20ac${bestUC.toFixed(2)}\u2013\u20ac${(d.worst?.uc || 0).toFixed(2)} (${Math.round(d.spread)}\u00d7).`)
    if (d.hasPriceGap) {
      const bestLabel = d.best?.desc ? `${d.best.pn} (${d.best.desc})` : d.best?.pn
      let simSummary = ''
      if (highSimCount > 0) simSummary = ` Similarity analysis: <span class="step-data">${highSimCount} parts are \u226570% similar</span> to the reference \u2014 price differences are likely negotiable.`
      if (lowSimCount > 0) simSummary += ` ${lowSimCount} part${lowSimCount > 1 ? 's' : ''} score below 40% \u2014 may have legitimate engineering differences.`
      steps.push(`<strong>Verify engineering equivalence:</strong> Compare each part\u2019s specs to the best-priced reference (${bestLabel}). Focus on material, process, tolerance, and size.${simSummary} See the Data Pack for the full side-by-side comparison with similarity scores.`)
      steps.push(`<strong>Set the target price:</strong> Best-in-family is <span class="step-data">${bestLabel} at \u20ac${bestUC.toFixed(2)}</span>. Use this as the anchor. For parts with higher EAU, expect even lower pricing. For lower EAU, allow up to +40% volume adjustment.`)
      if (top3.length > 0) {
        const topList = top3.map(p => `${p.desc ? p.pn + ' (' + p.desc + ')' : p.pn}: \u20ac${p.uc.toFixed(2)} \u2192 target \u20ac${bestUC.toFixed(2)} (${formatEur(p.savingsPot)} potential)`).join('; ')
        steps.push(`<strong>Prioritize high-impact parts:</strong> Start with these: ${topList}.`)
      }
      steps.push(`<strong>Challenge suppliers:</strong> "We buy ${d.partCount} similar parts in the same process family. Our best price is \u20ac${bestUC.toFixed(2)}. Please explain why this part is ${Math.round(d.spread)}\u00d7 higher, or align pricing within 2 weeks."`)
    }
    if (d.hasConsolidation) {
      steps.push(`<strong>Bundle the RFQ:</strong> Consolidate all ${d.partCount} parts into a single RFQ package. Present full family volume (<span class="step-data">${formatEur(d.totalSpend)}</span>) to ${top2}. Ask for family-rate pricing.`)
      steps.push(`<strong>Negotiate volume tiers:</strong> "If we consolidate the full ${formatEur(d.totalSpend)} with you, what family discount can you offer?" Target 8\u201315% improvement on volume alone.`)
    }
    steps.push(`<strong>Set timeline & escalation:</strong> Request revised pricing within 2 weeks. If no movement, issue competitive RFQ to 2\u20133 alternates for the full family.`)
    return steps
  }
  if (opp.type === 'site_gap') {
    const sites = d.sites || []
    const worst = sites[0]
    const sgLabel = d.desc ? `${d.pn} (${d.desc})` : (opp.parts[0]?.description ? `${opp.parts[0].pn} (${opp.parts[0].description})` : opp.parts[0]?.pn)
    return [
      `<strong>Confirm it's the same part:</strong> ${sgLabel} is purchased at ${sites.length} sites — verify identical spec and revision across ${sites.map(s => s.site).slice(0, 4).join(', ')}.`,
      `<strong>Establish the benchmark:</strong> Best in-network price is <span class="step-data">${formatEur(d.best || 0)}/unit</span> at ${d.bestSite}. That's your internal target — already achieved, no negotiation needed to prove it.`,
      `<strong>Tackle the worst gap first:</strong> ${worst ? `${worst.site} pays €${worst.unit_cost.toFixed(2)} (+${Math.round((worst.unit_cost / d.best - 1) * 100)}%)` : ''}. Greatest savings per unit of effort.`,
      `<strong>Harmonize contracts:</strong> Extend the best site's price/terms network-wide, or consolidate the volume of all ${sites.length} sites under one agreement.`,
      `<strong>Target outcome:</strong> Pull every site to best-in-network — <span class="step-data">${formatRange(opp.estSavings)}</span> annual savings with no requalification.`,
    ]
  }
  return []
}

/**
 * Generate expanded context data for each action plan step.
 * Returns an array of expansion objects (one per step). Each has:
 *   { tables: [{title, headers, rows}], bullets: string[], emailDraft: string|null, drawings: [{pn, fileId, filename}] }
 * Only populated for negotiation type currently.
 */
export function generateExpandedStepData(opp) {
  if (opp.type !== 'negotiation') return []
  const d = opp.vizData || {}
  const items = d.items || []
  const goodItems = d.goodItems || []
  const parts = opp.parts || []
  const supplierName = d.supplierName || ''
  const nt = opp._negotiationTarget || 1.1
  const ntPct = Math.round((nt - 1) * 100)
  const expansions = []

  // Helper: compute floored target for an item (same logic as negotiation trigger)
  const flooredTarget = (i) => {
    if (!i.bm || i.bm <= 0) return null
    const rawT = i.bm * nt
    const peers = (i.cohort_entries || []).filter(e => e.unit_cost > 0)
    const cheap = peers.length ? peers.reduce((a, b) => (a.unit_cost < b.unit_cost ? a : b)) : null
    const floor = cheap ? cheap.unit_cost * volAdjust(cheap.eau || 0, parseFloat(i.eau) || 0) : rawT
    return Math.max(rawT, floor)
  }

  // Step 0: Strategy recommendation
  if (opp.strategies && opp.strategies.length > 0) {
    const strat = opp.strategies
    const topStrat = strat[0]
    expansions.push({
      tables: [{
        title: 'Strategy Score Breakdown',
        headers: ['Strategy', 'Score', 'Fit'],
        rows: strat.map(s => [
          `${s.icon} ${s.name}`,
          `${s.score}/100`,
          s.score >= 70 ? 'Recommended' : s.score >= 50 ? 'Viable' : s.score >= 30 ? 'Consider' : 'Not recommended',
        ]),
      }, {
        title: `Score Factors: ${topStrat.icon} ${topStrat.name} (${topStrat.score}/100)`,
        headers: ['Signal', 'Points'],
        rows: (topStrat.factors || []).map(f => [f.signal, f.pts > 0 ? `+${f.pts}` : String(f.pts)]),
      }, ...(strat[1] && strat[1].score >= 50 ? [{
        title: `Score Factors: ${strat[1].icon} ${strat[1].name} (${strat[1].score}/100)`,
        headers: ['Signal', 'Points'],
        rows: (strat[1].factors || []).map(f => [f.signal, f.pts > 0 ? `+${f.pts}` : String(f.pts)]),
      }] : [])],
      bullets: [
        `Primary strategy: ${topStrat.name} — ${topStrat.desc}`,
        ...strat.filter(s => s.score >= 50 && s !== strat[0]).map(s => `Alternative: ${s.name} (${s.score}/100) — ${s.desc}`),
        ...strat.filter(s => s.score < 30).map(s => `Not recommended: ${s.name} — insufficient data signals or poor fit for this supplier profile.`),
      ],
      emailDraft: null,
      drawings: [],
    })
  }

  // Step 1: Map the exposure
  const totalGoodSpend = goodItems.reduce((s, g) => s + g.spend, 0)
  const totalRelationship = opp.spendExposed + totalGoodSpend
  expansions.push({
    tables: [{
      title: 'Full Supplier Relationship',
      headers: ['Category', 'Parts', 'Spend', '% of Total'],
      rows: [
        ['Overpriced (>1.3x)', String(items.length), formatEur(opp.spendExposed), `${Math.round(opp.spendExposed / totalRelationship * 100)}%`],
        ['Competitive (\u22641.3x)', String(goodItems.length), formatEur(totalGoodSpend), `${Math.round(totalGoodSpend / totalRelationship * 100)}%`],
        ['Total relationship', String(items.length + goodItems.length), formatEur(totalRelationship), '100%'],
      ],
    }, {
      title: 'Overpriced Parts Detail',
      headers: ['Part #', 'Description', 'UC', 'Benchmark', 'PP', 'EAU', 'Spend', 'Overspend'],
      rows: items.map(i => [
        i.pn, i.desc || '\u2014',
        `\u20AC${i.uc.toFixed(2)}`, i.bm > 0 ? `\u20AC${i.bm.toFixed(2)}` : '\u2014',
        `${i.pp.toFixed(2)}x`, i.eau > 0 ? Math.round(i.eau).toLocaleString() : '\u2014',
        formatEur(i.spend), formatEur(i.spend * (1 - nt / i.pp)),
      ]),
    }],
    bullets: [
      `Total annual business with ${supplierName}: ${formatEur(totalRelationship)} (${items.length + goodItems.length} line items).`,
      `${Math.round(opp.spendExposed / totalRelationship * 100)}% of spend is above benchmark — this is the negotiation target.`,
      goodItems.length > 0 ? `${goodItems.length} parts are priced competitively — proof this supplier CAN deliver fair pricing.` : null,
    ].filter(Boolean),
    emailDraft: null,
    drawings: items.map(i => {
      const p = parts.find(pt => pt.pn === i.pn)
      return p ? { pn: i.pn, fileId: p.file_id || null, filename: p.filename || '' } : null
    }).filter(Boolean),
  })

  // Step 2: Prioritize highest-overspend
  const top5 = items.slice(0, 5)
  expansions.push({
    tables: [{
      title: 'Top Parts by Overspend',
      headers: ['Part #', 'Description', 'UC', 'Benchmark', 'PP', 'Overspend', `Target (${nt}x)`, 'Savings @ Target'],
      rows: top5.map(i => {
        const target = flooredTarget(i)
        const savings = target ? i.spend * (1 - target / i.uc) : 0
        return [
          i.pn, i.desc || '\u2014',
          `\u20AC${i.uc.toFixed(2)}`, i.bm > 0 ? `\u20AC${i.bm.toFixed(2)}` : '\u2014',
          `${i.pp.toFixed(2)}x`, formatEur(i.spend * (1 - nt / i.pp)),
          target ? `\u20AC${target.toFixed(2)}` : '\u2014', formatEur(savings),
        ]
      }),
    }, {
      title: 'Benchmark Methodology',
      headers: ['Part #', 'Description', 'Cohort', 'Cohort Size', 'Median UC', 'Volume Adj.'],
      rows: top5.map(i => {
        const p = parts.find(pt => pt.pn === i.pn)
        const sup = p?.suppliers?.find(s => s.name === supplierName)
        return [
          i.pn,
          i.desc || p?.description || '\u2014',
          humanizeCohortName(sup?.cohort_name),
          String(sup?.cohort_size || '\u2014'),
          sup?.cohort_median_uc ? `\u20AC${sup.cohort_median_uc.toFixed(2)}` : '\u2014',
          sup?.cohort_median_eau ? `EAU ${Math.round(sup.cohort_median_eau).toLocaleString()}` : '\u2014',
        ]
      }),
    }],
    bullets: [
      `Benchmark formula: Median UC \u00D7 volAdjust(Median EAU, Part EAU) \u2014 adjusts for volume differences (exponent fitted from your data).`,
      `Each cohort groups parts by: manufacturing method, material, size, tolerance, and complexity.`,
      top5[0]?.bm > 0 ? `Example: ${top5[0].pn} (${top5[0].desc || '\u2014'}) benchmark \u20AC${top5[0].bm.toFixed(2)} based on ${parts.find(pt => pt.pn === top5[0].pn)?.suppliers?.find(s => s.name === supplierName)?.cohort_size || '?'}-member cohort.` : null,
    ].filter(Boolean),
    emailDraft: null,
    drawings: top5.map(i => {
      const p = parts.find(pt => pt.pn === i.pn)
      return p ? { pn: i.pn, fileId: p.file_id || null, filename: p.filename || '' } : null
    }).filter(Boolean),
  })

  // Step 3: Flag critical outliers (only if any)
  const highPP = items.filter(i => i.pp > 1.5)
  if (highPP.length > 0) {
    expansions.push({
      tables: [{
        title: 'Critical Outliers (>1.5x benchmark)',
        headers: ['Part #', 'Description', 'UC', 'Benchmark', 'PP', 'Cohort', 'Cohort Size'],
        rows: highPP.map(i => {
          const p = parts.find(pt => pt.pn === i.pn)
          const sup = p?.suppliers?.find(s => s.name === supplierName)
          return [
            i.pn, i.desc || '\u2014',
            `\u20AC${i.uc.toFixed(2)}`, i.bm > 0 ? `\u20AC${i.bm.toFixed(2)}` : '\u2014',
            `${i.pp.toFixed(2)}x`,
            humanizeCohortName(sup?.cohort_name),
            String(sup?.cohort_size || '\u2014'),
          ]
        }),
      }, {
        title: 'Cohort Peers (Proof of Market Rate)',
        headers: ['Part #', 'Description', 'Supplier', 'Unit Cost', 'EAU'],
        rows: highPP.slice(0, 3).flatMap(i => {
          const p = parts.find(pt => pt.pn === i.pn)
          const sup = p?.suppliers?.find(s => s.name === supplierName)
          return (sup?.cohort_entries || []).filter(e => e.supplier !== supplierName).slice(0, 3).map(e => [
            e.pn || '\u2014', e.description || '\u2014', e.supplier || '\u2014',
            `\u20AC${e.unit_cost.toFixed(2)}`, e.eau > 0 ? Math.round(e.eau).toLocaleString() : '\u2014',
          ])
        }),
      }],
      bullets: (() => {
        const topOutlier = highPP[0]
        const topPart = parts.find(pt => pt.pn === topOutlier.pn)
        const topSup = topPart?.suppliers?.find(s => s.name === supplierName)
        const cohortLabel = humanizeCohortName(topSup?.cohort_name) || 'unknown'
        const cohortSize = topSup?.cohort_size || '?'
        return [
          `${highPP.length} part${highPP.length > 1 ? 's' : ''} exceed 1.5x — these are not minor variances. They require specific justification from the supplier.`,
          `Question to ask: "What specific engineering requirements justify ${Math.round((topOutlier.pp - 1) * 100)}% above comparable parts made by other suppliers in the [${cohortLabel}] cohort (${cohortSize} members)?"`,
          `If no valid justification: these are pricing anomalies, not cost-driven premiums.`,
        ]
      })(),
      emailDraft: null,
      drawings: highPP.map(i => {
        const p = parts.find(pt => pt.pn === i.pn)
        return p ? { pn: i.pn, fileId: p.file_id || null, filename: p.filename || '' } : null
      }).filter(Boolean),
    })
  }

  // Step 4: Acknowledge competitive items (only if any)
  if (goodItems.length > 0) {
    expansions.push({
      tables: [{
        title: 'Competitive Items from Same Supplier',
        headers: ['Part #', 'Description', 'UC', 'Benchmark', 'PP', 'EAU', 'Spend'],
        rows: goodItems.map(g => [
          g.pn, g.desc || '\u2014',
          `\u20AC${g.uc.toFixed(2)}`, g.bm > 0 ? `\u20AC${g.bm.toFixed(2)}` : '\u2014',
          `${g.pp.toFixed(2)}x`, g.eau > 0 ? Math.round(g.eau).toLocaleString() : '\u2014',
          formatEur(g.spend),
        ]),
      }, {
        title: 'Attribute Comparison: Competitive vs. Overpriced',
        headers: ['Attribute', 'Competitive Items', 'Overpriced Items'],
        rows: (() => {
          const compMats = [...new Set(goodItems.map(g => g.material || '').filter(Boolean))]
          const overMats = [...new Set(items.map(i => { const p = parts.find(pt => pt.pn === i.pn); return p?.material || '' }).filter(Boolean))]
          const compMethods = [...new Set(goodItems.map(g => g.method || '').filter(Boolean))]
          const overMethods = [...new Set(items.map(i => { const p = parts.find(pt => pt.pn === i.pn); return p?.method || '' }).filter(Boolean))]
          return [
            ['Materials', compMats.join(', ') || '\u2014', overMats.join(', ') || '\u2014'],
            ['Methods', compMethods.join(', ') || '\u2014', overMethods.join(', ') || '\u2014'],
            ['Avg PP', `${(goodItems.reduce((s, g) => s + g.pp, 0) / goodItems.length).toFixed(2)}x`, `${(items.reduce((s, i) => s + i.pp, 0) / items.length).toFixed(2)}x`],
            ['Total Spend', formatEur(totalGoodSpend), formatEur(opp.spendExposed)],
          ]
        })(),
      }],
      bullets: [
        `${goodItems.length} parts at/below benchmark (\u20AC${totalGoodSpend.toLocaleString()} spend) — supplier IS competitive on these.`,
        `Key talking point: "You price ${goodItems.length} items competitively using the same processes. What justifies the premium on these ${items.length} outliers?"`,
        `If same material and method: there is no valid cost justification for the gap.`,
      ],
      emailDraft: null,
      drawings: [],
    })
  }

  // Step 5: Set your target
  expansions.push({
    tables: [{
      title: 'Target Price Table',
      headers: ['Part #', 'Description', 'Current UC', 'Benchmark', `Target (${nt}x)`, 'Savings/Unit', 'EAU', 'Annual Savings'],
      rows: items.map(i => {
        const target = flooredTarget(i)
        const savingsUnit = target ? i.uc - target : 0
        const annSavings = target && i.eau > 0 ? savingsUnit * i.eau : (target ? i.spend * (1 - target / i.uc) : 0)
        return [
          i.pn, i.desc || '\u2014',
          `\u20AC${i.uc.toFixed(2)}`, i.bm > 0 ? `\u20AC${i.bm.toFixed(2)}` : '\u2014',
          target ? `\u20AC${target.toFixed(2)}` : '\u2014',
          target ? `\u20AC${savingsUnit.toFixed(2)}` : '\u2014',
          i.eau > 0 ? Math.round(i.eau).toLocaleString() : '\u2014',
          formatEur(annSavings),
        ]
      }),
    }, {
      title: 'Sensitivity Analysis',
      headers: ['Target Level', 'Premium over BM', 'Total Savings', 'Savings %'],
      rows: [1.0, 1.05, 1.1, 1.15, 1.2, 1.3].map(mult => {
        const totalSav = items.reduce((s, i) => {
          if (!i.bm || i.bm <= 0) return s
          const target = i.bm * mult
          if (target >= i.uc) return s
          return s + i.spend * (1 - target / i.uc)
        }, 0)
        return [
          `${mult.toFixed(2)}x`, `+${Math.round((mult - 1) * 100)}%`,
          formatEur(totalSav), `${Math.round(totalSav / opp.spendExposed * 100)}% of spend`,
        ]
      }),
    }],
    bullets: [
      `Target: ${nt}x benchmark = ${ntPct}% premium to account for service/quality/relationship factors.`,
      `Total potential at ${nt}x target: ${formatEur(items.reduce((s, i) => i.bm > 0 ? s + i.spend * (1 - (i.bm * nt) / i.uc) : s, 0))}.`,
      `Floor reference: cheapest cohort peer sets the absolute floor. ${nt}x is a fair, defensible ask.`,
    ],
    emailDraft: null,
    drawings: [],
  })

  // Step 6: Challenge the supplier (email draft)
  const totalOverspend = items.reduce((s, i) => s + i.spend * (1 - nt / i.pp), 0)
  expansions.push({
    tables: [],
    bullets: [
      `Tone: Collaborative but firm. Lead with data, not demands.`,
      `Attach: The price analysis table from this data pack.`,
      `Request: Revised pricing within 2 weeks (14 calendar days).`,
      `Escalation: If no response within 2 weeks, schedule a QBR. If no movement after QBR, proceed to competitive sourcing.`,
    ],
    emailDraft: `Subject: Pricing Review Request \u2014 ${items.length} Line Items\n\nDear ${supplierName} Team,\n\nAs part of our ongoing supply chain optimization, we have completed a benchmarking analysis of our portfolio with your company.\n\nOur analysis shows ${items.length} parts priced ${Math.round(((opp.avgPP || 1) - 1) * 100)}% above comparable market rates (${formatEur(opp.spendExposed)} annual spend). We have detailed benchmarking data for each line item and would like to discuss alignment.\n\n${goodItems.length > 0 ? `We note that ${goodItems.length} other items in our portfolio with you are priced competitively \u2014 we value the relationship and want to resolve this collaboratively.\n\n` : ''}We propose:\n1. Review the attached price position analysis\n2. Provide revised pricing for highlighted items within 2 weeks\n3. Schedule a brief call to discuss any items requiring clarification\n\nOur target is ${formatEur(items.reduce((s, i) => i.bm > 0 ? s + i.spend * (1 - (i.bm * nt) / i.uc) : s, 0))} annual savings, which we believe is achievable while maintaining fair margins on your side.\n\nPlease let us know your availability for a brief discussion.\n\nBest regards`,
    drawings: [],
  })

  // Step 7: Leverage total volume
  expansions.push({
    tables: [{
      title: 'Volume Commitment Package',
      headers: ['Item', 'Value'],
      rows: [
        ['Overpriced parts (negotiation target)', `${items.length} parts, ${formatEur(opp.spendExposed)}`],
        ['Competitive parts (retention commitment)', `${goodItems.length} parts, ${formatEur(totalGoodSpend)}`],
        ['Total volume at stake', formatEur(totalRelationship)],
        ['Your ask', `Align overpriced parts to ${nt}x benchmark`],
        ['Your offer', `Commit ${formatEur(totalRelationship)} annual volume for 2\u20133 year term`],
      ],
    }],
    bullets: [
      `Bundle message: "We're not just asking for a price cut \u2014 we're offering volume certainty in exchange for fair pricing."`,
      `If ${items.length} parts align: commit the full ${formatEur(totalRelationship)} portfolio.`,
      `If they refuse: the ${formatEur(opp.spendExposed)} in overpriced spend goes to RFQ within 30 days.`,
      goodItems.length > 0 ? `Reminder: "${goodItems.length} parts prove you can be competitive. Extend that discipline and you keep everything."` : null,
    ].filter(Boolean),
    emailDraft: null,
    drawings: [],
  })

  // Step 8: Walk-away position
  const altSuppliers = new Set()
  items.forEach(i => {
    const p = parts.find(pt => pt.pn === i.pn)
    const sup = p?.suppliers?.find(s => s.name === supplierName)
    ;(sup?.cohort_entries || []).forEach(e => {
      if (e.supplier && e.supplier !== supplierName) altSuppliers.add(e.supplier)
    })
  })
  expansions.push({
    tables: [{
      title: 'Alternative Suppliers (from cohort data)',
      headers: ['Supplier', 'Seen in Cohorts'],
      rows: [...altSuppliers].slice(0, 10).map(name => [name, 'Yes']),
    }],
    bullets: [
      `${altSuppliers.size} alternative supplier${altSuppliers.size !== 1 ? 's' : ''} identified from cohort benchmarking data.`,
      `Walk-away threshold: 1.3x benchmark. Anything above this after 2-week deadline triggers competitive RFQ.`,
      `RFQ scope: ${items.length} parts, ${formatEur(opp.spendExposed)} annual volume.`,
      `Timeline: 2-week response deadline \u2192 2-week RFQ \u2192 4-week qualification = parts can move in ~8 weeks.`,
    ],
    emailDraft: null,
    drawings: [],
  })

  return expansions
}

// ─── Phase 5: Null-Baseline Honesty Check ─────────────────────────────────────

/** Mulberry32 seeded PRNG — 4 lines, no deps */
function mulberry32(seed) {
  return function() {
    seed |= 0; seed = seed + 0x6D2B79F5 | 0
    let t = Math.imul(seed ^ seed >>> 15, 1 | seed)
    t = t + Math.imul(t ^ t >>> 7, 61 | t) ^ t
    return ((t ^ t >>> 14) >>> 0) / 4294967296
  }
}

/** Fisher-Yates shuffle with seeded PRNG */
function seededShuffle(arr, rng) {
  const a = [...arr]
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(rng() * (i + 1))
    ;[a[i], a[j]] = [a[j], a[i]]
  }
  return a
}

/**
 * Compute a null baseline by shuffling unit costs within each part family,
 * re-running the pipeline, and averaging across seeds.
 * Returns mean totals per lever from shuffled data.
 *
 * @param {Array} parts - enriched parts (from computePricePositions)
 * @param {Object} params - same params as computeOpportunities
 * @param {Array} seeds - PRNG seeds (default [1,2,3])
 * @returns {{ negotiation: number, part_family: number, seeds: number }}
 */
export function computeNullBaseline(parts, params = {}, seeds = [1, 2, 3]) {
  const { similarMinAttrs = 4 } = params
  const totals = { negotiation: 0, part_family: 0 }

  // Build family groups (same logic as computeOpportunities §3)
  const familyGroups = {}
  parts.forEach(p => {
    if (p.method && p.method.toLowerCase().includes('assembl')) return
    const attrs = [p.method, p.material, p.size, p.tolerance_band, p.complexity_tier].filter(v => !isUnknownValue(v))
    if (attrs.length < similarMinAttrs) return
    if (!p.suppliers.some(s => s.unit_cost > 0 && s.spend > 0)) return
    const famKey = attrs.join('|')
    if (!familyGroups[famKey]) familyGroups[famKey] = []
    familyGroups[famKey].push(p)
  })

  seeds.forEach(seed => {
    const rng = mulberry32(seed)

    // Deep-copy parts, shuffle unit_costs within each family (keep spend fixed)
    const shuffled = parts.map(p => ({
      ...p,
      suppliers: p.suppliers.map(s => ({ ...s })),
    }))

    // For each family, collect all unit_costs, shuffle, redistribute
    Object.values(familyGroups).forEach(famParts => {
      // Collect indices of all priced supplier entries in this family
      const entries = []
      famParts.forEach(origP => {
        const p = shuffled.find(sp => sp.pn === origP.pn)
        if (!p) return
        p.suppliers.forEach((s, si) => {
          if (s.unit_cost > 0 && s.spend > 0) entries.push({ p, si })
        })
      })
      if (entries.length < 2) return

      // Shuffle unit_costs
      const ucs = entries.map(e => e.p.suppliers[e.si].unit_cost)
      const shuffledUcs = seededShuffle(ucs, rng)
      entries.forEach((e, i) => { e.p.suppliers[e.si].unit_cost = shuffledUcs[i] })
    })

    // Re-enrich and compute opportunities on shuffled data
    const enrichedShuffled = computePricePositions(shuffled)
    const opps = computeOpportunities(enrichedShuffled, params)

    // Sum by lever (only negotiation and part_family)
    opps.forEach(o => {
      if (o.quarantined) return
      if (o.type === 'negotiation') totals.negotiation += o.estSavings
      else if (o.type === 'part_family') totals.part_family += o.estSavings
    })
  })

  // Average across seeds
  const n = seeds.length
  return {
    negotiation: totals.negotiation / n,
    part_family: totals.part_family / n,
    seeds: n,
  }
}

// ─── Phase 8: Supplier Negotiation Helper ─────────────────────────────────────

/**
 * Build a comprehensive negotiation dossier for a supplier's entire book.
 * @param {Array} enrichedParts - from computePricePositions
 * @param {string} supplierName - supplier to negotiate with
 * @param {Object} params - tunables
 * @returns {Object} dossier with overview, tierA, tierB, tierC, validate, ask, strategies
 */
export function computeSupplierNegotiation(enrichedParts, supplierName, params = {}) {
  const {
    negotiationTarget = 1.1,
    sigmaThreshold = 1.0,
    validateRatio = 4.0,
    portfolioPct = 0.02,
    askCapPct = 0.07,
  } = params

  // Gather all lines for this supplier
  const lines = []
  enrichedParts.forEach(p => {
    (p.suppliers || []).forEach(s => {
      if (s.name === supplierName && s.unit_cost > 0) {
        lines.push({ part: p, supplier: s })
      }
    })
  })
  if (!lines.length) return null

  const totalSpend = lines.reduce((s, l) => s + (l.supplier.spend || 0), 0)
  const partCount = lines.length
  const families = new Set(lines.map(l => [l.part.method, l.part.material, l.part.size].filter(Boolean).join('|')))
  const sites = new Set()
  lines.forEach(l => (l.supplier.sites || []).forEach(site => sites.add(site)))

  // Overview: identify competitively-priced items (PROTECT list)
  const goodItems = []
  const overItems = []
  lines.forEach(l => {
    const s = l.supplier
    const pp = s.price_position_v2 || s.price_position
    if (pp && pp <= 1.0 && s.spend > 0) {
      goodItems.push({ pn: l.part.pn, desc: l.part.description || '', pp, spend: s.spend, uc: s.unit_cost })
    }
  })

  // Dataset total spend for share calculation
  const datasetSpend = enrichedParts.reduce((s, p) => s + (p.total_spend || 0), 0)
  const shareOfCategory = datasetSpend > 0 ? totalSpend / datasetSpend : 0

  // ── Tier A: observed same-part evidence ──
  // Lines where another supplier or site buys the SAME PN cheaper (volume-adjusted)
  const tierA = []
  lines.forEach(l => {
    const p = l.part
    const s = l.supplier
    const uc = s.unit_cost
    const eau = parseFloat(s.eau) || 0
    const volExp = s.vol_exp || p._volExp || VOLUME_EXP

    // Check other suppliers on this same PN
    const otherSuppliers = (p.suppliers || []).filter(os => os.name !== supplierName && os.unit_cost > 0)
    if (!otherSuppliers.length) return

    const best = otherSuppliers.reduce((a, b) => {
      const aAdj = a.unit_cost * volAdjust(parseFloat(a.eau) || 0, eau, volExp)
      const bAdj = b.unit_cost * volAdjust(parseFloat(b.eau) || 0, eau, volExp)
      return aAdj < bAdj ? a : b
    })
    const bestAdjUc = best.unit_cost * volAdjust(parseFloat(best.eau) || 0, eau, volExp)

    if (uc > bestAdjUc * 1.05) { // at least 5% gap
      const gap = uc / bestAdjUc
      const saving = s.spend * (1 - bestAdjUc / uc)
      const quarantined = gap >= validateRatio
      tierA.push({
        pn: p.pn, desc: p.description || '', uc, spend: s.spend, eau,
        bestAltUc: Math.round(bestAdjUc * 100) / 100, bestAltName: best.name,
        kind: 'same_pn_supplier', gap: Math.round(gap * 100) / 100, saving, quarantined,
      })
    }
  })

  // Also check site gaps on the same PN
  lines.forEach(l => {
    const p = l.part
    const s = l.supplier
    const uc = s.unit_cost
    const eau = parseFloat(s.eau) || 0
    const volExp = s.vol_exp || p._volExp || VOLUME_EXP

    ;(p.sites || []).forEach(site => {
      if ((site.suppliers || []).includes(supplierName)) return // this supplier's own site
      if (!(site.unit_cost > 0) || site.unit_cost >= uc) return
      const adjUc = site.unit_cost * volAdjust(parseFloat(site.eau) || 0, eau, volExp)
      if (uc > adjUc * 1.05) {
        const gap = uc / adjUc
        // Avoid duplicate if already in tierA
        if (tierA.some(a => a.pn === p.pn && a.kind === 'same_pn_supplier')) return
        const saving = s.spend * (1 - adjUc / uc)
        tierA.push({
          pn: p.pn, desc: p.description || '', uc, spend: s.spend, eau,
          bestAltUc: Math.round(adjUc * 100) / 100, bestAltName: site.site,
          kind: 'same_pn_site', gap: Math.round(gap * 100) / 100, saving, quarantined: gap >= validateRatio,
        })
      }
    })
  })
  tierA.sort((a, b) => b.saving - a.saving)

  // ── Tier B: model-inferred (should-cost residual) ──
  const tierB = []
  lines.forEach(l => {
    const s = l.supplier
    if (s.sc_residual == null || s.sc_sigma == null || s.sc_sigma <= 0) return
    if (s.sc_residual <= sigmaThreshold * s.sc_sigma) return
    // Skip items already in tierA (don't double-count)
    if (tierA.some(a => a.pn === l.part.pn && !a.quarantined)) return
    const sc = s.should_cost || 0
    if (sc <= 0) return
    const saving = s.spend * (1 - sc / s.unit_cost)
    tierB.push({
      pn: l.part.pn, desc: l.part.description || '', uc: s.unit_cost,
      should_cost: sc, sc_residual: s.sc_residual, spend: s.spend, saving,
    })
  })
  tierB.sort((a, b) => b.saving - a.saving)

  // ── Tier C: carrot (bundle), volume, BATNA ──
  // Bundle: parts at OTHER suppliers where THIS supplier is at/below should-cost
  const bundle = []
  enrichedParts.forEach(p => {
    // Check if this supplier has a competitive position in this part's cohort
    const ourLine = (p.suppliers || []).find(s => s.name === supplierName)
    if (ourLine) return // we already supply this part
    // Check if we're competitive in the cohort (via cohort entries)
    const firstSup = (p.suppliers || []).find(s => s.cohort_entries && s.unit_cost > 0)
    if (!firstSup) return
    const ourEntry = (firstSup.cohort_entries || []).find(e => e.supplier === supplierName)
    if (!ourEntry) return
    // Is our cohort entry at/below benchmark?
    const bm = firstSup.benchmark || firstSup.cohort_median_uc
    if (!bm || ourEntry.unit_cost > bm) return
    bundle.push({
      pn: p.pn, desc: p.description || '', currentSupplier: firstSup.name || '', spend: p.total_spend || 0,
    })
  })
  bundle.sort((a, b) => b.spend - a.spend)

  const consolidatedSpend = bundle.reduce((s, b) => s + b.spend, 0)

  // BATNA: walk-away = computeSupplierExit summary
  const exitPlan = computeSupplierExit(enrichedParts, supplierName, params)
  const batna = {
    redistributablePct: exitPlan.vizData.totalExposure > 0
      ? exitPlan.vizData.redistributableSpend / exitPlan.vizData.totalExposure : 0,
    redistributableSpend: exitPlan.vizData.redistributableSpend,
    receiverCount: exitPlan.vizData.receivers?.length || 0,
    netFloor: exitPlan.vizData.netFloor || 0,
  }

  // ── Ask ladder ──
  const tierAValue = tierA.filter(a => !a.quarantined).reduce((s, a) => s + a.saving, 0)
  const tierBValue = tierB.reduce((s, b) => s + b.saving, 0)
  const tierCValue = consolidatedSpend * portfolioPct

  let opening = tierAValue + 0.5 * tierBValue + (bundle.length > 0 ? portfolioPct * totalSpend : 0)
  let target = 0.9 * tierAValue + 0.35 * tierBValue
  let walkaway = 0.8 * tierAValue
  const cap = askCapPct * totalSpend

  // Scale if opening exceeds cap
  if (cap > 0 && opening > cap) {
    const scale = cap / opening
    opening *= scale
    target *= scale
    walkaway *= scale
  }

  const ask = { tierAValue, tierBValue, tierCValue, opening, target, walkaway, cap }

  // ── Validate: quarantined items ──
  const validate = tierA.filter(a => a.quarantined)
  const activeTierA = tierA.filter(a => !a.quarantined)

  // ── Strategies ──
  // Build g/goodItems shapes for scoreNegotiationStrategies
  const g = {
    supplier: supplierName,
    items: activeTierA.concat(tierB).map(i => ({
      part: enrichedParts.find(p => p.pn === i.pn) || {},
      supplier: lines.find(l => l.part.pn === i.pn)?.supplier || {},
      overspend: i.saving,
    })),
    totalOverspend: tierAValue + tierBValue,
    totalSpend,
  }
  const strategies = scoreNegotiationStrategies(g, goodItems, enrichedParts)

  return {
    type: 'supplier_negotiation',
    supplier: supplierName,
    overview: {
      totalSpend, partCount, familyCount: families.size, siteCount: sites.size,
      sites: [...sites], shareOfCategory, goodItems: goodItems.slice(0, 10),
    },
    tierA: activeTierA,
    tierB,
    tierC: { bundle: bundle.slice(0, 20), volume: { consolidatedSpend, message: bundle.length > 0 ? `${bundle.length} parts (${formatEur(consolidatedSpend)}) available to award as consolidation carrot.` : 'No competitive positions in other parts.' }, batna },
    validate,
    ask,
    strategies,
    estSavings: ask.target,
    evidenceTier: tierAValue > tierBValue ? 'A' : 'B',
    effort: 2,
  }
}
